import React, { useState, useRef, useEffect, useCallback } from "react";
import { Routes, Route, useNavigate, useParams, useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
// Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Leaflet Map
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});
import {
  FlaskConical,
  Send,
  Paperclip,
  Bot,
  Copy,
  Download,
  Check,
  Loader2,
  Sparkles,
  Share2,
  Upload,
  X,
  Wrench,
  ChevronDown,
  Coffee,
  // Text Tool Icons (1열)
  PenLine,
  Heading,
  Newspaper,
  FileEdit,
  Globe,
  Landmark,
  Building2,
  FileText,
  FileBox,
  Lightbulb,
  FileSignature,
  // Visual Tool Icons (2열)
  Table,
  BarChart3,
  BarChart,
  BarChart2,
  LineChart,
  PieChart,
  LayoutDashboard,
  Image,
  Clock,
  MapPin,
  GitCompare,
  TrendingUp,
  Workflow,
  Columns,
  Camera,
  Palette,
  Shapes,
  // Audio Tool Icons (3열)
  Volume2,
  Podcast,
  Radio,
  Smile,
  Briefcase,
  Users,
  MessageCircle,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  // Visual Template Gallery Icons
  Scale,
  Trophy,
  ClipboardList,
  Activity,
  Hash,
  GitBranch,
  ArrowLeftRight,
  Hexagon,
  Circle as CircleIcon,
  History,
  Target,
  CalendarDays,
  Eye,
  Map as MapIcon,
  Flame,
  Navigation,
  // PRISM Audience Icons
  GraduationCap,
  Rocket,
  Globe2,
  Crown,
  Wallet,
  Home,
  // Content Tools (웹툰, 유튜브)
  Clapperboard,
  Youtube,
  LayoutGrid,
  BookOpen,
  HelpCircle,
  Type,
  AlignLeft,
  Package,
  Pencil,
  Save,
  XCircle,
  Zap,
  Crop,
  ScanLine,
  Scissors,
} from "lucide-react";

// Tool Icon Renderer
const ICON_MAP = {
  // Text Tools (1열)
  PenLine,
  Heading,
  Newspaper,
  FileEdit,
  Globe,
  Landmark,
  Building2,
  FileText,
  FileBox,
  Bot,
  Lightbulb,
  FileSignature,
  // Visual Tools (2열)
  Table,
  BarChart3,
  BarChart,
  BarChart2,
  LineChart,
  PieChart,
  LayoutDashboard,
  Image,
  Clock,
  MapPin,
  GitCompare,
  TrendingUp,
  Workflow,
  Columns,
  Camera,
  Palette,
  Shapes,
  Crop,
  ScanLine,
  Scissors,
  // Audio Tools (3열)
  Volume2,
  Podcast,
  Radio,
  Smile,
  Briefcase,
  Users,
  MessageCircle,
  // Visual Template Gallery Icons
  Scale,
  Trophy,
  ClipboardList,
  Activity,
  Hash,
  GitBranch,
  ArrowLeftRight,
  Sparkles,
  Hexagon,
  Circle: CircleIcon,
  History,
  Target,
  CalendarDays,
  Telescope: Eye,
  Map: MapIcon,
  Flame,
  Route: Navigation,
  // PRISM Audience Icons
  GraduationCap,
  Rocket,
  Globe2,
  Crown,
  Wallet,
  Home,
  // Content Tools (웹툰, 유튜브)
  Clapperboard,
  Youtube,
  LayoutGrid,
  BookOpen,
  HelpCircle,
  Type,
  AlignLeft,
  Package,
  Zap,
};

const ToolIcon = ({ name, className = "w-4 h-4" }) => {
  const IconComponent = ICON_MAP[name];
  if (!IconComponent) return null;
  return <IconComponent className={className} />;
};
import { Toaster, toast } from "react-hot-toast";
import { parseSpreadsheetFile } from "./utils/csvParser";

import Layout from "./features/layout/Layout";
import { LoginContainer, SignUpPage, ProtectedRoute } from "./features/auth";
import TeamLibrary from "./features/team/components/TeamLibrary";
import ProjectList from "./features/projects/components/ProjectList";
import ProjectDetail from "./features/projects/components/ProjectDetail";
import WorkDetail from "./features/team/components/WorkDetail";
import ServiceGuide from "./features/guide/components/ServiceGuide";
import AdminPage from "./features/admin/components/AdminPage";
import UsageDashboard from "./features/admin/components/UsageDashboard";
import PrismCanvas from "./pages/PrismCanvas";
import { useChatStore } from "./stores/chatStore";
import { useSidebarStore } from "./stores/sidebarStore";
import { useTeamStore } from "./stores/teamStore";
import { processWithBedrock, isConfigured as isBedrockConfigured, prewarmConnection } from "./services/bedrockService";
import { conversationService } from "./services/conversationService";
import geminiImageService from "./services/geminiImageService";
import elevenLabsService from "./services/elevenLabsService";
import { exportToExcel, exportToCSV, copyToClipboard, copyTableAsText } from "./utils/exportUtils";
import { TOOLS, TOOL_LIST, TEXT_TOOL_LIST, VISUAL_TOOL_LIST, AUDIO_TOOL_LIST, getToolById, getToolColor, isAudioTool } from "./config/tools";
import { AI_MODELS, DEFAULT_MODEL, getModelById } from "./config/models";
import { ModelIcon } from "./components/ModelIcons";
import { VISUAL_TEMPLATES, isVisualTool, getTemplatesForTool } from "./config/visualTemplates";
import TutorialGuide from "./components/TutorialGuide";
import PodcastFunnel from "./components/PodcastFunnel";
import ChartConverter from "./components/ChartConverter";
import ChartSlicer from "./components/ChartSlicer";
import SubTypeSelector from "./components/SubTypeSelector";
import { useSmoothStreaming } from "./hooks/useSmoothStreaming";

// 현재 로그인한 사용자 ID 가져오기 (Cognito sub)
const getCurrentUserId = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo');
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr);
      const userId = userInfo.userId || userInfo.sub || null;
      if (userId) return userId;
    }
  } catch (error) {
    console.warn('[Auth] Failed to get user ID from localStorage:', error);
  }

  // Env var fallback (dev)
  const fallbackId = import.meta.env.VITE_USER_ID || null;
  if (fallbackId) return fallbackId;

  // Anonymous user: generate and persist a stable ID
  let anonId = localStorage.getItem('anonUserId');
  if (!anonId) {
    anonId = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem('anonUserId', anonId);
  }
  return anonId;
};

// 💧 Drop Skeleton - 똑똑똑 떨어지는 재미있는 로딩 애니메이션
const ChatSkeleton = () => {
  // 컨테이너 애니메이션 (빠르게)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06,
        delayChildren: 0.02,
      }
    },
  };

  // 떨어지는 애니메이션 (빠르고 탄력있게)
  const dropVariants = {
    hidden: { opacity: 0, y: -16 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 25,
      }
    },
  };

  return (
    <motion.div
      className="flex flex-col h-full"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-12 space-y-10 w-full">

          {/* 1. 사용자 메시지 */}
          <motion.div variants={dropVariants} className="flex justify-end">
            <div className="space-y-2.5 max-w-[60%]">
              <div className="h-5 bg-sedaily-500/60 rounded-lg w-56 ml-auto animate-pulse" />
              <div className="h-5 bg-sedaily-500/60 rounded-lg w-40 ml-auto animate-pulse" />
            </div>
          </motion.div>

          {/* 2. AI 메시지 */}
          <motion.div variants={dropVariants} className="flex gap-3">
            <div className="w-9 h-9 rounded-full bg-sedaily-500/50 flex-shrink-0 animate-pulse" />
            <div className="flex-1 space-y-2.5">
              <div className="h-5 bg-gray-400/40 rounded-lg w-[85%] animate-pulse" />
              <div className="h-5 bg-gray-400/40 rounded-lg w-full animate-pulse" />
              <div className="h-5 bg-gray-400/40 rounded-lg w-[75%] animate-pulse" />
              <div className="h-5 bg-gray-400/40 rounded-lg w-[60%] animate-pulse" />
            </div>
          </motion.div>

          {/* 3. 사용자 메시지 */}
          <motion.div variants={dropVariants} className="flex justify-end">
            <div className="space-y-2.5 max-w-[60%]">
              <div className="h-5 bg-sedaily-500/60 rounded-lg w-48 ml-auto animate-pulse" />
            </div>
          </motion.div>

          {/* 4. AI 메시지 */}
          <motion.div variants={dropVariants} className="flex gap-3">
            <div className="w-9 h-9 rounded-full bg-sedaily-500/50 flex-shrink-0 animate-pulse" />
            <div className="flex-1 space-y-2.5">
              <div className="h-5 bg-gray-400/40 rounded-lg w-[90%] animate-pulse" />
              <div className="h-5 bg-gray-400/40 rounded-lg w-[80%] animate-pulse" />
              <div className="h-5 bg-gray-400/40 rounded-lg w-[65%] animate-pulse" />
            </div>
          </motion.div>

        </div>
      </div>

      {/* Input area skeleton */}
      <div className="border-t border-bg-300/20 p-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            variants={dropVariants}
            className="h-12 bg-gray-400/30 rounded-2xl animate-pulse"
          />
        </div>
      </div>
    </motion.div>
  );
};

// Visual Data Renderer Component
const VisualDataRenderer = ({ visualData }) => {
  if (!visualData || !visualData.data) return null;

  const { type, data, generatedImage, imageError } = visualData;

  // Chart Renderer with Chart.js
  if (type === 'chart' && data) {
    const chartType = data.chartType || 'bar';

    // Default color palette
    const defaultColors = [
      'rgba(139, 92, 246, 0.8)',   // violet
      'rgba(59, 130, 246, 0.8)',   // blue
      'rgba(16, 185, 129, 0.8)',   // emerald
      'rgba(245, 158, 11, 0.8)',   // amber
      'rgba(239, 68, 68, 0.8)',    // red
      'rgba(236, 72, 153, 0.8)',   // pink
      'rgba(6, 182, 212, 0.8)',    // cyan
      'rgba(132, 204, 22, 0.8)',   // lime
    ];

    const borderColors = defaultColors.map(c => c.replace('0.8', '1'));

    // Prepare chart data
    const chartData = {
      labels: data.labels || [],
      datasets: (data.datasets || []).map((ds, idx) => ({
        label: ds.label || `Dataset ${idx + 1}`,
        data: ds.data || [],
        backgroundColor: ds.backgroundColor || (chartType === 'pie' || chartType === 'doughnut'
          ? defaultColors.slice(0, ds.data?.length || 0)
          : defaultColors[idx % defaultColors.length]),
        borderColor: ds.borderColor || (chartType === 'line'
          ? borderColors[idx % borderColors.length]
          : 'transparent'),
        borderWidth: chartType === 'line' ? 2 : 1,
        fill: chartType === 'line' ? false : undefined,
        tension: chartType === 'line' ? 0.3 : undefined,
        pointBackgroundColor: chartType === 'line' ? borderColors[idx % borderColors.length] : undefined,
        pointRadius: chartType === 'line' ? 4 : undefined,
      })),
    };

    // Chart options
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 11 },
          },
        },
        title: {
          display: !!data.title,
          text: data.title,
          color: 'rgba(255, 255, 255, 0.9)',
          font: { size: 14, weight: 'bold' },
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 10,
          cornerRadius: 8,
        },
      },
      scales: chartType !== 'pie' && chartType !== 'doughnut' ? {
        x: {
          title: {
            display: !!data.options?.xAxisLabel,
            text: data.options?.xAxisLabel || '',
            color: 'rgba(255, 255, 255, 0.6)',
          },
          ticks: { color: 'rgba(255, 255, 255, 0.6)' },
          grid: { color: 'rgba(255, 255, 255, 0.1)' },
        },
        y: {
          title: {
            display: !!data.options?.yAxisLabel,
            text: data.options?.yAxisLabel || '',
            color: 'rgba(255, 255, 255, 0.6)',
          },
          ticks: { color: 'rgba(255, 255, 255, 0.6)' },
          grid: { color: 'rgba(255, 255, 255, 0.1)' },
          beginAtZero: true,
        },
      } : undefined,
    };

    // Render appropriate chart type
    const renderChart = () => {
      switch (chartType) {
        case 'line':
          return <Line data={chartData} options={chartOptions} />;
        case 'pie':
          return <Pie data={chartData} options={chartOptions} />;
        case 'doughnut':
          return <Doughnut data={chartData} options={chartOptions} />;
        case 'bar':
        default:
          return <Bar data={chartData} options={chartOptions} />;
      }
    };

    return (
      <motion.div
        className="mt-4 p-4 bg-bg-300/30 rounded-xl"
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-violet-400" />
          <span className="text-sm font-medium text-text-200">Chart</span>
          <span className="text-xs px-2 py-0.5 rounded bg-violet-500/20 text-violet-400">
            {chartType === 'bar' ? 'Bar' : chartType === 'line' ? 'Line' : chartType === 'pie' ? 'Pie' : chartType}
          </span>
        </div>

        {/* Chart Container */}
        <motion.div
          className="bg-bg-200/50 rounded-lg p-4"
          style={{ maxHeight: '400px' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          {renderChart()}
        </motion.div>

        {/* Data Summary */}
        <motion.div
          className="mt-3 pt-3 border-t border-bg-300/30"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <div className="flex flex-wrap gap-2 text-xs text-text-400">
            <span>Data points: {data.labels?.length || 0}</span>
            <span>•</span>
            <span>Datasets: {data.datasets?.length || 0}</span>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // Image Renderer - 🍌 Gemini 이미지 생성 지원
  if (type === 'image') {
    return (
      <motion.div
        className="mt-4 p-4 bg-bg-300/30 rounded-xl"
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Image className="w-5 h-5 text-fuchsia-400" />
          <span className="text-sm font-medium text-text-200">🍌 AI Image</span>
          <span className="text-xs px-2 py-0.5 rounded bg-fuchsia-500/20 text-fuchsia-400">AI Generated</span>
        </div>
        {generatedImage?.url ? (
          <div className="space-y-3">
            <img
              src={generatedImage.url}
              alt={data?.description || 'AI generated image'}
              className="w-full max-w-2xl rounded-lg shadow-lg"
            />
            {generatedImage.description && (
              <p className="text-sm text-text-300">{generatedImage.description}</p>
            )}
            {data?.style && (
              <p className="text-xs text-text-500">Style: {data.style}</p>
            )}
            <div className="flex items-center gap-3">
              <a
                href={generatedImage.url}
                download={`image-${Date.now()}.png`}
                className="inline-flex items-center gap-1 text-xs text-fuchsia-400 hover:text-fuchsia-300 bg-fuchsia-500/10 px-3 py-1.5 rounded-lg"
              >
                <Download className="w-3 h-3" /> Download
              </a>
              <button
                onClick={() => {
                  if (generatedImage?.url) {
                    navigator.clipboard.writeText(generatedImage.url)
                      .then(() => toast.success('URL copied'))
                      .catch(() => toast.error('Copy failed'));
                  }
                }}
                className="inline-flex items-center gap-1 text-xs text-text-400 hover:text-text-300 bg-bg-200/50 px-3 py-1.5 rounded-lg"
              >
                <Copy className="w-3 h-3" /> Copy URL
              </button>
            </div>
            <p className="text-xs text-amber-500/80">※ Citation required when using AI-generated images.</p>
          </div>
        ) : imageError ? (
          <div className="p-4 bg-red-500/10 rounded-lg">
            <p className="text-sm text-red-400">Image generation failed: {imageError}</p>
            <p className="text-xs text-text-500 mt-2">Please try again or modify your prompt.</p>
          </div>
        ) : (
          <div className="text-sm text-text-300">
            <p className="mb-2">Prompt: {data?.prompt?.substring(0, 100)}...</p>
            <p className="text-xs text-text-500">Waiting for image generation...</p>
          </div>
        )}
      </motion.div>
    );
  }

  // Infographic Renderer - 🍌 Gemini 이미지 생성 지원
  if (type === 'infographic') {
    return (
      <motion.div
        className="mt-4 p-4 bg-bg-300/30 rounded-xl"
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <div className="flex items-center gap-2 mb-3">
          <LayoutDashboard className="w-5 h-5 text-orange-400" />
          <span className="text-sm font-medium text-text-200">🍌 Infographic</span>
          <span className="text-xs px-2 py-0.5 rounded bg-orange-500/20 text-orange-400">AI Generated</span>
        </div>

        {/* Generated Image (Priority) */}
        {generatedImage?.url ? (
          <div className="space-y-3">
            <img
              src={generatedImage.url}
              alt={data?.title || 'Infographic'}
              className="w-full max-w-2xl rounded-lg shadow-lg"
            />
            {generatedImage.description && (
              <p className="text-sm text-text-300">{generatedImage.description}</p>
            )}
            <div className="flex items-center gap-3">
              <a
                href={generatedImage.url}
                download={`infographic-${Date.now()}.png`}
                className="inline-flex items-center gap-1 text-xs text-orange-400 hover:text-orange-300 bg-orange-500/10 px-3 py-1.5 rounded-lg"
              >
                <Download className="w-3 h-3" /> Download
              </a>
              <button
                onClick={() => {
                  if (generatedImage?.url) {
                    navigator.clipboard.writeText(generatedImage.url)
                      .then(() => toast.success('URL copied'))
                      .catch(() => toast.error('Copy failed'));
                  }
                }}
                className="inline-flex items-center gap-1 text-xs text-text-400 hover:text-text-300 bg-bg-200/50 px-3 py-1.5 rounded-lg"
              >
                <Copy className="w-3 h-3" /> Copy URL
              </button>
            </div>
            <p className="text-xs text-amber-500/80">※ AI-generated infographic. Data verification required.</p>
          </div>
        ) : imageError ? (
          <div className="p-4 bg-red-500/10 rounded-lg">
            <p className="text-sm text-red-400">Image generation failed: {imageError}</p>
            <p className="text-xs text-text-500 mt-2">Please try again or modify your prompt.</p>
          </div>
        ) : data ? (
          /* Fallback: Data-based rendering (when no image is generated) */
          <div>
            <p className="font-medium text-text-100 mb-2">{data.title}</p>
            {data.subtitle && <p className="text-sm text-text-300 mb-3">{data.subtitle}</p>}
            <div className="space-y-2">
              {data.sections?.map((section, i) => (
                <div key={i} className="p-3 bg-bg-200/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{section.icon === 'chart' ? '📊' : section.icon === 'money' ? '💰' : section.icon === 'users' ? '👥' : '📌'}</span>
                    <span className="font-medium text-text-100">{section.title}</span>
                  </div>
                  <div className="text-xl font-bold text-sedaily-400">{section.value}</div>
                  <p className="text-xs text-text-400">{section.description}</p>
                </div>
              ))}
            </div>
            {data.footer && <p className="text-xs text-text-500 mt-3">{data.footer}</p>}
          </div>
        ) : (
          <div className="text-sm text-text-400">
            <p>Waiting for infographic generation...</p>
          </div>
        )}
      </motion.div>
    );
  }

  // Timeline Renderer
  if (type === 'timeline' && data) {
    return (
      <motion.div
        className="mt-4 p-4 bg-bg-300/30 rounded-xl"
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-5 h-5 text-teal-400" />
          <span className="text-sm font-medium text-text-200">Timeline</span>
        </div>
        <p className="font-medium text-text-100 mb-3">{data.title}</p>
        <div className="relative pl-4 border-l-2 border-teal-500/30 space-y-4">
          {data.events?.map((event, i) => (
            <motion.div
              key={i}
              className="relative"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: i * 0.1 }}
            >
              <div className="absolute -left-[21px] w-3 h-3 rounded-full bg-teal-500"></div>
              <div className="text-xs text-teal-400 mb-1">{event.date}</div>
              <div className="font-medium text-text-100">{event.title}</div>
              <p className="text-sm text-text-300">{event.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    );
  }

  // Map Renderer with Leaflet
  if (type === 'map' && data) {
    const center = data.center || { lat: 37.5665, lng: 126.9780 }; // Default: Seoul
    const zoom = data.zoom || 7;
    const locations = data.locations || [];
    const mapType = data.mapType || 'markers';

    // Color for markers/circles
    const getMarkerColor = (idx) => {
      const colors = ['#84CC16', '#3B82F6', '#EF4444', '#F59E0B', '#8B5CF6', '#EC4899'];
      return colors[idx % colors.length];
    };

    return (
      <motion.div
        className="mt-4 p-4 bg-bg-300/30 rounded-xl"
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-5 h-5 text-lime-400" />
          <span className="text-sm font-medium text-text-200">Map</span>
          <span className="text-xs px-2 py-0.5 rounded bg-lime-500/20 text-lime-400">
            {mapType === 'markers' ? 'Markers' : mapType === 'heatmap' ? 'Heatmap' : mapType}
          </span>
        </div>
        {data.title && <p className="font-medium text-text-100 mb-3">{data.title}</p>}

        {/* Leaflet Map */}
        <div className="rounded-lg overflow-hidden" style={{ height: '350px' }}>
          <MapContainer
            center={[center.lat, center.lng]}
            zoom={zoom}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {locations.map((loc, idx) => (
              loc.lat && loc.lng && (
                mapType === 'heatmap' ? (
                  <Circle
                    key={idx}
                    center={[loc.lat, loc.lng]}
                    radius={loc.value ? Math.min(loc.value * 100, 50000) : 10000}
                    pathOptions={{
                      color: getMarkerColor(idx),
                      fillColor: getMarkerColor(idx),
                      fillOpacity: 0.4,
                    }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong>{loc.name}</strong>
                        {loc.value && <div>Value: {loc.value}</div>}
                        {loc.description && <div className="text-gray-600">{loc.description}</div>}
                      </div>
                    </Popup>
                  </Circle>
                ) : (
                  <Marker key={idx} position={[loc.lat, loc.lng]}>
                    <Popup>
                      <div className="text-sm">
                        <strong>{loc.name}</strong>
                        {loc.value && <div>Value: {loc.value}</div>}
                        {loc.description && <div className="text-gray-600">{loc.description}</div>}
                        {loc.category && <div className="text-xs text-gray-500">{loc.category}</div>}
                      </div>
                    </Popup>
                  </Marker>
                )
              )
            ))}
          </MapContainer>
        </div>

        {/* Location List */}
        <div className="mt-3 pt-3 border-t border-bg-300/30">
          <p className="text-xs text-text-500 mb-2">Locations ({locations.length})</p>
          <div className="flex flex-wrap gap-2">
            {locations.slice(0, 6).map((loc, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded bg-bg-200/50 text-text-300">
                📍 {loc.name}
              </span>
            ))}
            {locations.length > 6 && (
              <span className="text-xs px-2 py-1 text-text-500">+{locations.length - 6} more</span>
            )}
          </div>
        </div>

        {/* Legend if available */}
        {data.legend && data.legend.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {data.legend.map((item, i) => (
              <span key={i} className="flex items-center gap-1 text-xs text-text-400">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></span>
                {item.label}
              </span>
            ))}
          </div>
        )}
      </motion.div>
    );
  }

  return null;
};

// 오디오 렌더러 컴포넌트 (TTS / 팟캐스트) - Web Audio API 사용
const AudioRenderer = ({ audioData }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [audioError, setAudioError] = useState(null);
  const [audioBuffer, setAudioBuffer] = useState(null);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const startTimeRef = useRef(0);
  const pausedAtRef = useRef(0);

  // 데이터 추출 (hooks 전에)
  const type = audioData?.type;
  const audioUrl = audioData?.audioUrl;
  const audioSegments = audioData?.audioSegments;
  const script = audioData?.script;
  const audioDuration = audioData?.audioDuration;
  const style = audioData?.style;
  const error = audioData?.error;

  // Web Audio API로 오디오 로드 (hooks는 항상 실행되어야 함)
  useEffect(() => {
    if (!audioUrl) return;

    const loadAudio = async () => {
      try {
        console.log('%c[Audio] Web Audio API로 로드 시작...', 'color: #3b82f6;', audioUrl);
        setIsLoading(true);
        setAudioError(null);

        // Fetch audio data
        const response = await fetch(audioUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

        const arrayBuffer = await response.arrayBuffer();
        console.log('%c[Audio] 다운로드 완료:', 'color: #22c55e;', `${(arrayBuffer.byteLength / 1024).toFixed(1)}KB`);

        // Create AudioContext
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }

        // Decode audio
        const decoded = await audioContextRef.current.decodeAudioData(arrayBuffer);
        console.log('%c[Audio] 디코딩 성공:', 'color: #22c55e;', {
          duration: decoded.duration.toFixed(1) + 's',
          sampleRate: decoded.sampleRate,
          channels: decoded.numberOfChannels
        });

        setAudioBuffer(decoded);
        setDuration(decoded.duration);
        setIsLoading(false);
      } catch (err) {
        console.error('%c[Audio] 로드 실패:', 'color: #ef4444;', err);
        setAudioError(`오디오 로드 실패: ${err.message}`);
        setIsLoading(false);
      }
    };

    loadAudio();

    return () => {
      if (sourceNodeRef.current) {
        try { sourceNodeRef.current.stop(); } catch (e) {}
      }
    };
  }, [audioUrl]);

  const togglePlay = async () => {
    if (!audioBuffer || !audioContextRef.current) {
      console.error('%c[Audio] 오디오 버퍼가 없음', 'color: #ef4444;');
      return;
    }

    if (isPlaying) {
      // 일시정지
      if (sourceNodeRef.current) {
        sourceNodeRef.current.stop();
        pausedAtRef.current = audioContextRef.current.currentTime - startTimeRef.current;
      }
      setIsPlaying(false);
    } else {
      // 재생
      try {
        // Resume context if suspended (autoplay policy)
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        // Create new source node
        const source = audioContextRef.current.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContextRef.current.destination);

        source.onended = () => {
          setIsPlaying(false);
          pausedAtRef.current = 0;
        };

        startTimeRef.current = audioContextRef.current.currentTime - pausedAtRef.current;
        source.start(0, pausedAtRef.current);
        sourceNodeRef.current = source;

        setIsPlaying(true);
        console.log('%c[Audio] 재생 시작', 'color: #22c55e;', { offset: pausedAtRef.current.toFixed(1) + 's' });
      } catch (err) {
        console.error('%c[Audio] 재생 실패:', 'color: #ef4444;', err);
        setAudioError(`재생 실패: ${err.message}`);
      }
    }
  };

  // 진행 시간 업데이트
  useEffect(() => {
    let animationId;
    const updateTime = () => {
      if (isPlaying && audioContextRef.current) {
        const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
        setCurrentTime(Math.min(elapsed, duration));
        animationId = requestAnimationFrame(updateTime);
      }
    };
    if (isPlaying) {
      animationId = requestAnimationFrame(updateTime);
    }
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [isPlaying, duration]);

  // audioData가 없으면 렌더링 안 함 (hooks 이후에 체크)
  if (!audioData) return null;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e) => {
    if (!audioBuffer || !audioContextRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const seekTime = percent * duration;

    if (sourceNodeRef.current) {
      try { sourceNodeRef.current.stop(); } catch (e) {}
    }

    pausedAtRef.current = seekTime;
    setCurrentTime(seekTime);

    if (isPlaying) {
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      source.onended = () => {
        setIsPlaying(false);
        pausedAtRef.current = 0;
      };
      startTimeRef.current = audioContextRef.current.currentTime - seekTime;
      source.start(0, seekTime);
      sourceNodeRef.current = source;
    }
  };

  // 에러 시 표시
  if (error) {
    return (
      <div className="mt-3 px-4 py-2 bg-red-500/10 rounded-lg">
        <p className="text-sm text-red-400">오디오 생성 실패</p>
      </div>
    );
  }

  // 로딩 또는 URL 없을 때
  if (!audioUrl) {
    return (
      <div className="mt-3 flex items-center gap-2 text-sm text-text-400">
        <div className="w-4 h-4 border-2 border-text-400 border-t-transparent rounded-full animate-spin" />
        <span>음성 생성 중...</span>
      </div>
    );
  }

  // 미니멀 오디오 플레이어 (TTS & Podcast 공통)
  const isPodcast = type === 'podcast';

  return (
    <motion.div
      className="mt-4"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
    >
      {/* 클린 미니멀 플레이어 */}
      <div className="flex items-center gap-4 px-4 py-3 bg-bg-200/60 rounded-2xl">

        {/* 재생 버튼 */}
        <button
          onClick={togglePlay}
          disabled={isLoading || audioError || !audioBuffer}
          className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${
            isLoading || audioError || !audioBuffer
              ? 'bg-bg-300 cursor-not-allowed'
              : 'bg-text-100 hover:bg-text-200 hover:scale-[1.02] active:scale-[0.98]'
          }`}
        >
          {isLoading ? (
            <div className="w-4 h-4 border-2 border-bg-100 border-t-transparent rounded-full animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-4 h-4 text-bg-100" />
          ) : (
            <Play className="w-4 h-4 text-bg-100 ml-0.5" />
          )}
        </button>

        {/* 프로그레스 바 + 시간 */}
        <div className="flex-1 flex items-center gap-3">
          <div
            className="flex-1 h-1 bg-bg-300/80 rounded-full cursor-pointer group"
            onClick={handleSeek}
          >
            <div
              className="h-full bg-text-300 rounded-full transition-all relative"
              style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-text-100 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm" />
            </div>
          </div>

          {/* 시간 표시 */}
          <span className="text-xs text-text-400 tabular-nums min-w-[65px] text-right">
            {formatTime(currentTime)} / {formatTime(duration || audioDuration || 0)}
          </span>
        </div>

        {/* 다운로드 버튼 */}
        <a
          href={audioUrl}
          download={`podcast-${Date.now()}.mp3`}
          className="p-2 rounded-lg hover:bg-bg-300/50 text-text-400 hover:text-text-200 transition-colors"
          title="다운로드"
        >
          <Download className="w-4 h-4" />
        </a>
      </div>

      {audioError && (
        <p className="mt-2 text-xs text-red-400/80">{audioError}</p>
      )}
    </motion.div>
  );
};

// 마크다운 기호 제거 (체크 이모지는 유지)
const cleanText = (text) => {
  if (!text) return '';
  return text
    // 이모지 제거 (체크 관련 제외: ✓✔✅☑)
    .replace(/[\u{1F600}-\u{1F64F}]/gu, '') // 얼굴
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, '') // 기호/그림
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '') // 교통
    .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '') // 국기
    // 체크 이모지 유지 (2600-26FF, 2700-27BF에서 체크만 제외)
    .replace(/[\u{2600}-\u{2610}]/gu, '')
    .replace(/[\u{2612}-\u{26FF}]/gu, '')
    .replace(/[\u{2700}-\u{2713}]/gu, '')
    .replace(/[\u{2715}-\u{27BF}]/gu, '')
    // 볼드/이탤릭 제거
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    // 수평선 제거
    .replace(/^[-_*]{3,}\s*$/gm, '')
    // 헤더 기호 제거
    .replace(/^#{1,6}\s+/gm, '')
    // 연속 빈 줄 정리
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

// 코드 블록 컴포넌트 (복사 버튼 포함)
const CodeBlock = ({ code, language }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Code copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="relative group my-3 rounded-xl overflow-hidden border border-bg-300/30">
      {/* 헤더 */}
      <div className="flex items-center justify-between px-4 py-2 bg-bg-300/50 border-b border-bg-300/30">
        <span className="text-xs text-text-400 font-mono">
          {language || 'code'}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-2 py-1 text-xs text-text-400 hover:text-text-200 hover:bg-bg-200/50 rounded transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-emerald-500">Copied</span>
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      {/* 코드 내용 */}
      <pre className="p-4 bg-bg-200/30 overflow-x-auto">
        <code className="text-sm font-mono text-text-200 whitespace-pre">
          {code}
        </code>
      </pre>
    </div>
  );
};

// 테이블 블록 컴포넌트 (복사 기능 포함)
const TableBlock = ({ headers, rows }) => {
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editHeaders, setEditHeaders] = useState(headers);
  const [editRows, setEditRows] = useState(rows.map(r => [...r]));
  const [savedHeaders, setSavedHeaders] = useState(headers);
  const [savedRows, setSavedRows] = useState(rows.map(r => [...r]));

  const handleCopy = async () => {
    try {
      const headerRow = savedHeaders.join('\t');
      const dataRows = savedRows.map(row => row.join('\t')).join('\n');
      const tableText = `${headerRow}\n${dataRows}`;
      await navigator.clipboard.writeText(tableText);
      setCopied(true);
      toast.success("Table copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Copy failed");
    }
  };

  const handleEditStart = () => {
    setEditHeaders([...savedHeaders]);
    setEditRows(savedRows.map(r => [...r]));
    setIsEditing(true);
  };

  const handleSave = () => {
    setSavedHeaders([...editHeaders]);
    setSavedRows(editRows.map(r => [...r]));
    setIsEditing(false);
    toast.success("Table saved");
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const displayHeaders = isEditing ? editHeaders : savedHeaders;
  const displayRows = isEditing ? editRows : savedRows;

  return (
    <div className="group relative my-4 rounded-xl border border-bg-300/40 bg-bg-200/20 overflow-hidden shadow-sm">
      {/* 헤더 바 */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-bg-300/40 to-bg-300/20 border-b border-bg-300/30">
        <div className="flex items-center gap-2">
          <Table className="w-4 h-4 text-sedaily-500" />
          <span className="text-xs font-medium text-text-300">{savedRows.length} rows</span>
          {isEditing && <span className="text-xs text-amber-500 font-medium">Editing</span>}
        </div>
        <div className="flex items-center gap-1">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all duration-200 bg-emerald-500/20 text-emerald-500 hover:bg-emerald-500/30"
              >
                <Save className="w-3.5 h-3.5" />
                <span>Save</span>
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all duration-200 text-text-400 hover:text-text-200 hover:bg-bg-300/60"
              >
                <XCircle className="w-3.5 h-3.5" />
                <span>Cancel</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleEditStart}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all duration-200 text-text-400 hover:text-text-200 hover:bg-bg-300/60"
              >
                <Pencil className="w-3.5 h-3.5" />
                <span>Edit</span>
              </button>
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all duration-200 ${
                  copied
                    ? 'bg-emerald-500/20 text-emerald-500'
                    : 'text-text-400 hover:text-text-200 hover:bg-bg-300/60'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    <span>Copied</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
      {/* 테이블 */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[400px]" style={{ fontFamily: '"Pretendard Variable", "Pretendard", -apple-system, sans-serif', fontSize: '15px' }}>
          <thead>
            <tr className="bg-bg-200/40">
              {displayHeaders.map((h, i) => (
                <th
                  key={i}
                  className="px-4 py-3 text-left font-semibold text-text-100 text-xs tracking-wide border-b border-bg-300/30"
                >
                  {isEditing ? (
                    <input
                      value={h}
                      onChange={e => {
                        const next = [...editHeaders];
                        next[i] = e.target.value;
                        setEditHeaders(next);
                      }}
                      className="w-full bg-bg-300/40 border border-bg-300/60 rounded px-2 py-1 text-xs font-semibold text-text-100 focus:outline-none focus:border-sedaily-500/60"
                    />
                  ) : h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, i) => (
              <tr
                key={i}
                className="transition-colors duration-150 hover:bg-sedaily-500/5 border-b border-bg-300/10 last:border-b-0"
              >
                {row.map((cell, j) => (
                  <td
                    key={j}
                    className={`px-4 py-3 text-text-200 ${j === 0 ? 'font-medium text-text-100' : ''}`}
                  >
                    {isEditing ? (
                      <input
                        value={cell}
                        onChange={e => {
                          const next = editRows.map(r => [...r]);
                          next[i][j] = e.target.value;
                          setEditRows(next);
                        }}
                        className="w-full bg-bg-300/40 border border-bg-300/60 rounded px-2 py-1 text-xs text-text-200 focus:outline-none focus:border-sedaily-500/60"
                      />
                    ) : cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// 코드 블록 파싱
const parseCodeBlocks = (text) => {
  if (!text) return null;

  const codeBlockRegex = /```(\w*)\n?([\s\S]*?)```/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    // 코드 블록 이전 텍스트
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index)
      });
    }
    // 코드 블록
    parts.push({
      type: 'code',
      language: match[1] || '',
      content: match[2].trim()
    });
    lastIndex = match.index + match[0].length;
  }

  // 마지막 텍스트
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex)
    });
  }

  return parts.length > 0 ? parts : null;
};

// 실시간 콘텐츠 렌더러 (텍스트 + 표 + 코드 블록)
const StreamingContent = ({ text, hasTable = false }) => {
  if (!text) {
    return (
      <div className="flex items-center gap-3 py-3">
        {/* 스파크 애니메이션 */}
        <div className="relative w-8 h-8">
          {/* 스파크 파티클들 */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1.5 h-1.5 rounded-full bg-sedaily-400"
              style={{ left: '50%', top: '50%', marginLeft: -3, marginTop: -3 }}
              animate={{
                x: [0, Math.cos(i * 60 * Math.PI / 180) * 16, 0],
                y: [0, Math.sin(i * 60 * Math.PI / 180) * 16, 0],
                opacity: [0, 1, 0],
                scale: [0.5, 1.2, 0.5]
              }}
              transition={{
                duration: 1.8,
                repeat: Infinity,
                delay: i * 0.12,
                ease: "easeOut"
              }}
            />
          ))}
        </div>
        {/* Text + dot animation */}
        <div className="flex items-center gap-1">
          <span className="text-sm text-text-300 font-medium">Thinking...</span>
          <div className="flex gap-0.5 ml-0.5">
            {[0, 1, 2].map((i) => (
              <motion.span
                key={i}
                className="w-1 h-1 rounded-full bg-text-400"
                animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
                transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2, ease: "easeInOut" }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // 🎨 Claude.ai 스타일 텍스트 (Crimson Text + 세련된 타이포그래피)
  const textStyle = "font-chat";

  const mdComponents = {
    h1: ({ children }) => <h1 className="text-xl font-bold text-text-100 mt-4 mb-2">{children}</h1>,
    h2: ({ children }) => <h2 className="text-base font-bold text-text-100 mt-4 mb-1.5">{children}</h2>,
    h3: ({ children }) => <h3 className="text-sm font-semibold text-text-100 mt-3 mb-1">{children}</h3>,
    strong: ({ children }) => <strong className="font-semibold text-text-100">{children}</strong>,
    ul: ({ children }) => <ul className="my-1.5 pl-4 space-y-0.5 list-disc">{children}</ul>,
    ol: ({ children }) => <ol className="my-1.5 pl-4 space-y-0.5 list-decimal">{children}</ol>,
    li: ({ children }) => <li className="text-text-200">{children}</li>,
    p: ({ children }) => <p className="my-1 leading-relaxed">{children}</p>,
  };

  // 코드 블록 파싱
  const codeBlocks = parseCodeBlocks(text);
  if (codeBlocks) {
    // hasTable이면 json 코드 블록 숨김 (이미 TableBlock으로 렌더링됨)
    const filteredBlocks = hasTable
      ? codeBlocks.filter(p => !(p.type === 'code' && p.language === 'json'))
      : codeBlocks;

    return (
      <div className={textStyle}>
        {filteredBlocks.map((part, index) => {
          if (part.type === 'code') {
            return <CodeBlock key={index} code={part.content} language={part.language} />;
          }
          // 텍스트 부분은 표/텍스트 세그먼트로 분리 렌더링
          const segments = splitTextByTables(part.content);
          if (segments.some(s => s.type === 'table')) {
            return (
              <div key={index}>
                {segments.map((seg, si) =>
                  seg.type === 'table'
                    ? <TableBlock key={si} headers={seg.headers} rows={seg.rows} />
                    : <ReactMarkdown key={si} remarkPlugins={[remarkGfm, remarkBreaks]} components={mdComponents}>{seg.content}</ReactMarkdown>
                )}
              </div>
            );
          }
          return (
            <ReactMarkdown key={index} remarkPlugins={[remarkGfm, remarkBreaks]} components={mdComponents}>
              {part.content}
            </ReactMarkdown>
          );
        })}
      </div>
    );
  }

  // 코드 블록이 없으면 표/텍스트 세그먼트로 분리 렌더링
  const segments = splitTextByTables(text);
  if (segments.some(s => s.type === 'table')) {
    return (
      <div className={`${textStyle} overflow-hidden`}>
        {segments.map((seg, i) =>
          seg.type === 'table'
            ? <TableBlock key={i} headers={seg.headers} rows={seg.rows} />
            : <ReactMarkdown key={i} remarkPlugins={[remarkGfm, remarkBreaks]} components={mdComponents}>{seg.content}</ReactMarkdown>
        )}
      </div>
    );
  }

  return (
    <div className={textStyle}>
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]} components={mdComponents}>{text}</ReactMarkdown>
    </div>
  );
};

// 마크다운 표 감지 및 파싱
// 셀 내 마크다운 인라인 문법 제거 (**bold**, *italic*, `code` 등)
const stripInlineMarkdown = (text) =>
  text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/~~(.+?)~~/g, '$1');

// 텍스트 내 모든 마크다운 표 파싱
const parseAllMarkdownTables = (text) => {
  if (!text) return [];
  const lines = text.split('\n');
  const tables = [];
  let inTable = false;
  let tableStart = -1;
  let tableLines = [];

  const finalizeTable = (endIdx) => {
    if (tableLines.length < 2) return;
    const headers = tableLines[0].split('|').filter(c => c.trim()).map(c => stripInlineMarkdown(c.trim()));
    if (!tableLines[1].match(/^\|[-:\s|]+\|$/)) return;
    const rows = [];
    for (let i = 2; i < tableLines.length; i++) {
      const cells = tableLines[i].split('|').filter(c => c.trim()).map(c => stripInlineMarkdown(c.trim()));
      if (cells.length > 0) rows.push(cells);
    }
    if (rows.length === 0) return;
    tables.push({ headers, rows, startLine: tableStart, endLine: endIdx });
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.startsWith('|') && line.endsWith('|')) {
      if (!inTable) { inTable = true; tableStart = i; tableLines = []; }
      tableLines.push(line);
    } else if (inTable && line.match(/^\|[-:\s|]+\|$/)) {
      tableLines.push(line);
    } else if (inTable) {
      finalizeTable(i);
      inTable = false; tableLines = []; tableStart = -1;
    }
  }
  if (inTable) finalizeTable(lines.length);
  return tables;
};

// 하위 호환용 단일 표 파싱
const parseMarkdownTable = (text) => {
  const tables = parseAllMarkdownTables(text);
  return tables.length > 0 ? tables[0] : null;
};

// 텍스트를 표/텍스트 세그먼트로 분리
const splitTextByTables = (text) => {
  const tables = parseAllMarkdownTables(text);
  if (tables.length === 0) return [{ type: 'text', content: text }];
  const lines = text.split('\n');
  const segments = [];
  let cursor = 0;
  for (const table of tables) {
    if (table.startLine > cursor) {
      const textContent = lines.slice(cursor, table.startLine).join('\n');
      if (textContent.trim()) segments.push({ type: 'text', content: textContent });
    }
    segments.push({ type: 'table', headers: table.headers, rows: table.rows });
    cursor = table.endLine;
  }
  if (cursor < lines.length) {
    const textContent = lines.slice(cursor).join('\n');
    if (textContent.trim()) segments.push({ type: 'text', content: textContent });
  }
  return segments;
};

// Message component
const Message = ({ message, onShare, streamingText }) => {
  const [copied, setCopied] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const downloadRef = useRef(null);

  // 다운로드 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setDownloadOpen(false);
      }
    };
    if (downloadOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [downloadOpen]);

  // ESC 키로 닫기
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setDownloadOpen(false);
      }
    };
    if (downloadOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [downloadOpen]);

  const handleCopy = async () => {
    if (message.table) {
      await copyTableAsText(message.table);
    } else {
      await copyToClipboard(message.content);
    }
    setCopied(true);
    toast.success("Copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (format) => {
    if (message.table) {
      if (format === "excel") {
        exportToExcel(message.table, "table-export");
        toast.success("Excel 파일이 다운로드되었습니다");
      } else if (format === "csv") {
        exportToCSV(message.table, "table-export");
        toast.success("CSV 파일이 다운로드되었습니다");
      }
    }
    setDownloadOpen(false);
  };

  if (message.role === "user") {
    // 팟캐스트 요청인지 확인
    const isPodcastRequest = message.content?.startsWith('[Podcast Request]') || message.content?.startsWith('[팟캐스트 생성]');

    if (isPodcastRequest) {
      // 팟캐스트 설정 파싱
      const lines = message.content.split('\n');
      const topic = lines.find(l => l.startsWith('주제:'))?.replace('주제:', '').trim() || '';
      const style = lines.find(l => l.startsWith('스타일:'))?.replace('스타일:', '').trim() || '';
      const duration = lines.find(l => l.startsWith('길이:'))?.replace('길이:', '').trim() || '';

      return (
        <div className="flex justify-end mb-8">
          <div className="max-w-[85%] bg-bg-200/80 backdrop-blur-sm rounded-2xl rounded-br-md px-5 py-4 shadow-sm">
            <div className="flex items-center gap-2.5 mb-3 text-text-200">
              <Podcast className="w-4 h-4 text-text-400" />
              <span className="text-sm font-medium">Podcast Request</span>
            </div>
            <p className="text-sm text-text-100 leading-relaxed line-clamp-3 mb-3">{topic}</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-400 px-2 py-1 bg-bg-300/50 rounded-md">{style}</span>
              <span className="text-xs text-text-400 px-2 py-1 bg-bg-300/50 rounded-md">{duration}</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-end mb-8">
        <div className="user-message-bubble">
          <p className="font-chat-user whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    );
  }

  // 팟캐스트 생성 중인지 확인
  const isPodcastGenerating = message.isStreaming && message.tool === 'podcast';

  return (
    <div className="mb-8">
      <div className="overflow-hidden">
          {message.isStreaming ? (
            isPodcastGenerating ? (
              /* 팟캐스트 생성 중 - 프로페셔널 애니메이션 UI */
              <div className="py-4 px-1">
                {/* 상단: 아이콘 + 제목 */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="relative">
                    {/* 외곽 링 애니메이션 */}
                    <motion.div
                      className="absolute -inset-1 rounded-full bg-gradient-to-r from-sedaily-500/30 via-purple-500/30 to-sedaily-500/30"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    />
                    <div className="relative w-12 h-12 rounded-full bg-gradient-to-br from-sedaily-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <Podcast className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-text-100">Generating Podcast</h4>
                    <p className="text-xs text-text-400 mt-0.5">AI is creating your content</p>
                  </div>
                </div>

                {/* 오디오 웨이브 애니메이션 */}
                <div className="flex items-center justify-center gap-[3px] h-8 mb-5">
                  {[...Array(12)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-1 bg-gradient-to-t from-sedaily-500 to-purple-500 rounded-full"
                      animate={{
                        height: [8, 20 + Math.random() * 12, 8],
                      }}
                      transition={{
                        duration: 0.8 + Math.random() * 0.4,
                        repeat: Infinity,
                        delay: i * 0.08,
                        ease: "easeInOut"
                      }}
                    />
                  ))}
                </div>

                {/* 진행 단계 */}
                <div className="space-y-2.5">
                  <AnimatePresence mode="popLayout">
                    {(() => {
                      const lines = streamingText?.split('\n').filter(line => line.trim()) || [];
                      const steps = [
                        { key: 'analyze', label: 'Analyzing topic', icon: '📊' },
                        { key: 'script', label: 'Writing dialogue script', icon: '✍️' },
                        { key: 'voice', label: 'Preparing voice synthesis', icon: '🎤' },
                        { key: 'audio', label: 'Generating audio', icon: '🎧' },
                      ];

                      // 현재 진행 중인 단계 파악 (스트리밍 텍스트 기반)
                      let currentStepIndex = 0;
                      const lastLine = lines[lines.length - 1]?.toLowerCase() || '';
                      if (lastLine.includes('스크립트') || lastLine.includes('대본') || lastLine.includes('작성')) currentStepIndex = 1;
                      if (lastLine.includes('음성') || lastLine.includes('polly') || lastLine.includes('전환')) currentStepIndex = 2;
                      if (lastLine.includes('오디오') || lastLine.includes('생성') || lastLine.includes('완료')) currentStepIndex = 3;

                      return steps.map((step, idx) => {
                        const isCompleted = idx < currentStepIndex;
                        const isActive = idx === currentStepIndex;
                        const isPending = idx > currentStepIndex;

                        return (
                          <motion.div
                            key={step.key}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1, duration: 0.3 }}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 ${
                              isActive
                                ? 'bg-sedaily-500/10 border border-sedaily-500/20'
                                : isCompleted
                                  ? 'bg-green-500/5'
                                  : 'opacity-40'
                            }`}
                          >
                            {/* 상태 아이콘 */}
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm ${
                              isCompleted
                                ? 'bg-green-500/20'
                                : isActive
                                  ? 'bg-sedaily-500/20'
                                  : 'bg-bg-300/50'
                            }`}>
                              {isCompleted ? (
                                <Check className="w-3.5 h-3.5 text-green-500" />
                              ) : isActive ? (
                                <motion.div
                                  animate={{ rotate: 360 }}
                                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                >
                                  <Loader2 className="w-3.5 h-3.5 text-sedaily-500" />
                                </motion.div>
                              ) : (
                                <span className="text-xs">{step.icon}</span>
                              )}
                            </div>

                            {/* 라벨 */}
                            <span className={`text-sm font-medium ${
                              isCompleted
                                ? 'text-green-500'
                                : isActive
                                  ? 'text-text-100'
                                  : 'text-text-400'
                            }`}>
                              {step.label}
                            </span>

                            {/* 활성 상태 표시 */}
                            {isActive && (
                              <motion.div
                                className="ml-auto flex gap-1"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                              >
                                {[0, 1, 2].map((dot) => (
                                  <motion.span
                                    key={dot}
                                    className="w-1 h-1 rounded-full bg-sedaily-500"
                                    animate={{ opacity: [0.3, 1, 0.3] }}
                                    transition={{ duration: 1, repeat: Infinity, delay: dot * 0.2 }}
                                  />
                                ))}
                              </motion.div>
                            )}
                          </motion.div>
                        );
                      });
                    })()}
                  </AnimatePresence>
                </div>

                {/* 하단 힌트 */}
                <motion.p
                  className="text-xs text-text-500 text-center mt-4"
                  animate={{ opacity: [0.5, 0.8, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  This may take about 30 seconds to 1 minute
                </motion.p>
              </div>
            ) : (
              /* 일반 스트리밍 중 - 실시간 표 감지 */
              <StreamingContent text={streamingText} />
            )
          ) : (
            <>
              {/* 완료됨 - 오디오가 있으면 텍스트 숨김 */}
              {!message.audioData && <StreamingContent text={message.content} hasTable={!!message.table} />}
              {message.table && (
                <div className="mt-2">
                  <TableBlock headers={message.table.headers} rows={message.table.rows} />
                </div>
              )}
              {/* Visual Data Renderer for charts, images, infographics, etc. */}
              {message.visualData && !message.table && (
                <VisualDataRenderer visualData={message.visualData} />
              )}
              {/* Audio Renderer for TTS, Podcast */}
              {message.audioData && (
                <AudioRenderer audioData={message.audioData} />
              )}
              {!message.isStreaming && message.content && (
                <div className="flex flex-wrap gap-1 mt-3 pt-2.5 border-t border-bg-300/50">
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-text-400 hover:text-text-200 hover:bg-bg-300/60 rounded-lg transition-colors"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? "Copied" : "Copy"}
                  </button>
                  {message.table && (
                    <div className="relative" ref={downloadRef}>
                      <button
                        onClick={() => setDownloadOpen(!downloadOpen)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-text-400 hover:text-text-200 hover:bg-bg-300/60 rounded-lg transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download
                      </button>
                      {downloadOpen && (
                        <div className="absolute left-0 top-full mt-1 bg-bg-100 rounded-lg z-10 py-1 min-w-[100px] overflow-hidden shadow-soft-xl">
                          <button
                            onClick={() => handleDownload("excel")}
                            className="w-full px-3 py-2 text-xs text-left text-text-300 hover:bg-bg-200 hover:text-text-100 transition-colors"
                          >
                            Excel (.xlsx)
                          </button>
                          <button
                            onClick={() => handleDownload("csv")}
                            className="w-full px-3 py-2 text-xs text-left text-text-300 hover:bg-bg-200 hover:text-text-100 transition-colors"
                          >
                            CSV (.csv)
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
    </div>
  );
};

// Tool Context Component - Shows examples/templates for selected tool
const ToolContext = ({ tool, onExampleClick, onSubTypeSelect, selectedExample }) => {
  if (!tool) return null;

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Sub-types if available (보도자료, 퇴고) */}
      {tool.subTypes && (
        <div className="flex flex-wrap gap-3 justify-center mb-6">
          {tool.subTypes.map((subType) => (
            <button
              key={subType.id}
              onClick={() => onSubTypeSelect && onSubTypeSelect(subType)}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-bg-200/60 hover:bg-bg-300/80 text-text-200 hover:text-text-100 transition-colors shadow-soft hover:shadow-soft-lg"
            >
              <span className="text-lg">{subType.icon}</span>
              <span className="text-sm font-medium">{subType.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Info list if available (제목 유형 등) */}
      {tool.info && (
        <div className="mb-6 p-4 rounded-xl bg-bg-200/40">
          <p className="text-xs text-text-400 mb-2">생성되는 {tool.name} 유형</p>
          <div className="flex flex-wrap gap-2">
            {tool.info.map((item, i) => (
              <span key={i} className="text-xs text-text-300 px-2 py-1 rounded-md bg-bg-300/50">
                {item}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Example prompts */}
      <div className="space-y-2">
        {tool.examples.map((example, i) => {
          const isSelected = selectedExample === example;
          return (
            <button
              key={i}
              onClick={() => onExampleClick && onExampleClick(example)}
              className={`w-full text-left text-sm px-4 py-3 rounded-xl transition-colors ${
                isSelected
                  ? 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30'
                  : 'text-text-300 hover:text-text-100 hover:bg-bg-200/60'
              }`}
            >
              {isSelected && <Check className="w-3.5 h-3.5 inline mr-2 text-purple-400" />}
              "{example}"
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Tool Button Component (unused - keeping for reference)
const ToolButton = ({ tool, isSelected, onClick }) => {
  return (
    <motion.button
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={onClick}
      className={`
        flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium
        transition-all duration-200
        ${isSelected
          ? 'bg-bg-300/80 text-text-100'
          : 'text-text-400 hover:bg-bg-300/50 hover:text-text-200'
        }
      `}
    >
      <ToolIcon name={tool.icon} className="w-4 h-4" />
      <span>{tool.name}</span>
    </motion.button>
  );
};

// Welcome Greeting - Gemini Style with Animation
const WelcomeGreeting = () => (
  <motion.div
    className="text-center mb-8"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, ease: "easeOut" }}
  >
    <motion.h1
      className="text-2xl font-medium tracking-wide flex items-center justify-center gap-2 text-text-100"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      <span>Take a breath</span>
      <Coffee className="w-5 h-5 text-amber-500" />
      <span>Grab a coffee, I've got this</span>
    </motion.h1>
  </motion.div>
);

// Tool Buttons Below Input - Two Row Layout with Animations
const ToolButtonsRow = ({ selectedTool, onToolSelect, selectedSubType, onSubTypeSelect }) => {
  // 드롭다운 열림 상태 관리
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // 드롭다운 밖 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      // 드롭다운이 열려있고, 클릭한 곳이 드롭다운 영역이 아니면 닫기
      if (openDropdownId) {
        const dropdownEl = document.querySelector(`[data-dropdown-id="${openDropdownId}"]`);
        if (dropdownEl && !dropdownEl.contains(event.target)) {
          setOpenDropdownId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  // 버튼 애니메이션 variants
  const buttonVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    tap: { scale: 0.97 },
    hover: { scale: 1.02 }
  };

  // 서브타입 확장 애니메이션 (사용 안함)
  const subTypeContainerVariants = {
    hidden: { opacity: 0, height: 0, marginTop: 0 },
    visible: {
      opacity: 1,
      height: 'auto',
      marginTop: 8,
      transition: { duration: 0.2, ease: 'easeOut' }
    },
    exit: {
      opacity: 0,
      height: 0,
      marginTop: 0,
      transition: { duration: 0.15, ease: 'easeIn' }
    }
  };

  // Coming Soon 클릭 핸들러
  const handleComingSoonClick = (tool) => {
    toast(`${tool.name} — Coming Soon`, {
      icon: '✨',
      duration: 2000,
      position: 'top-right',
      style: {
        borderRadius: '12px',
        background: 'rgba(30, 30, 35, 0.95)',
        color: '#e5e5e5',
        fontSize: '13px',
        padding: '10px 16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
      },
    });
  };

  // 도구 버튼 렌더링 함수
  const renderToolButton = (tool, index, colorTheme, baseDelay = 0) => {
    const isComingSoon = tool.comingSoon;
    const hasSubTypes = tool.subTypes && tool.subTypes.length > 0;
    const isSelected = selectedTool?.id === tool.id;

    // 색상 테마 설정
    const colorClasses = {
      blue: {
        active: 'bg-blue-500/15 text-blue-600 border border-blue-400/40 dark:bg-blue-400/20 dark:text-blue-300 dark:border-blue-400/40',
        inactive: 'bg-slate-500/8 text-text-300 border border-blue-400/30 hover:bg-blue-500/10 hover:text-blue-600 dark:bg-slate-700/30 dark:text-slate-200 dark:border-blue-400/25 dark:hover:bg-blue-400/15 dark:hover:text-blue-300',
        icon: 'text-blue-500/70 dark:text-blue-400/80'
      },
      violet: {
        active: 'bg-violet-500/15 text-violet-600 border border-violet-400/40 dark:bg-violet-400/20 dark:text-violet-300 dark:border-violet-400/40',
        inactive: 'bg-slate-500/8 text-text-300 border border-violet-400/30 hover:bg-violet-500/10 hover:text-violet-600 dark:bg-slate-700/30 dark:text-slate-200 dark:border-violet-400/25 dark:hover:bg-violet-400/15 dark:hover:text-violet-300',
        icon: 'text-violet-500/70 dark:text-violet-400/80'
      },
      emerald: {
        active: 'bg-emerald-500/15 text-emerald-600 border border-emerald-400/40 dark:bg-emerald-400/20 dark:text-emerald-300 dark:border-emerald-400/40',
        inactive: 'bg-slate-500/8 text-text-300 border border-emerald-400/30 hover:bg-emerald-500/10 hover:text-emerald-600 dark:bg-slate-700/30 dark:text-slate-200 dark:border-emerald-400/25 dark:hover:bg-emerald-400/15 dark:hover:text-emerald-300',
        icon: 'text-emerald-500/70 dark:text-emerald-400/80'
      }
    };

    const theme = colorClasses[colorTheme];

    if (isComingSoon) {
      return (
        <motion.button
          key={tool.id}
          onClick={() => handleComingSoonClick(tool)}
          variants={buttonVariants}
          initial="initial"
          animate="animate"
          whileHover={{ scale: 1.02 }}
          whileTap="tap"
          transition={{ duration: 0.2, delay: baseDelay + index * 0.03 }}
          className="group relative flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium
            bg-slate-500/5 text-text-300/70 hover:text-text-200/90
            dark:bg-slate-700/20 dark:text-slate-300 dark:hover:text-slate-200
            transition-all duration-200"
        >
          <ToolIcon name={tool.icon} className="w-4 h-4 opacity-50 group-hover:opacity-70" />
          <span>{tool.name}</span>
          {tool.badge && <span className="text-xs opacity-40">{tool.badge}</span>}
          {/* 호버 툴팁 */}
          <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md
            bg-gray-900 text-white text-xs whitespace-nowrap
            opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none
            shadow-lg">
            Coming Soon
          </span>
        </motion.button>
      );
    }

    // 드롭다운이 열려있는지 확인
    const isDropdownOpen = openDropdownId === tool.id;

    // 버튼 클릭 핸들러
    const handleButtonClick = () => {
      if (hasSubTypes) {
        // SubType이 있는 도구: 드롭다운 토글
        setOpenDropdownId(isDropdownOpen ? null : tool.id);
        if (!isSelected) {
          onToolSelect(tool); // 도구 선택
        }
      } else {
        // SubType이 없는 도구: 단순 선택
        onToolSelect(tool);
      }
    };

    // 서브타입이 있는 도구는 드롭다운과 함께 렌더링
    return (
      <div key={tool.id} className="relative" data-dropdown-id={tool.id}>
        <motion.button
          onClick={handleButtonClick}
          variants={buttonVariants}
          initial="initial"
          animate="animate"
          whileHover="hover"
          whileTap="tap"
          transition={{ duration: 0.2, delay: baseDelay + index * 0.03 }}
          className={`
            flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium
            transition-colors duration-200
            ${isSelected ? theme.active : theme.inactive}
          `}
        >
          <ToolIcon name={tool.icon} className={`w-4 h-4 ${isSelected ? '' : theme.icon}`} />
          <span>{tool.name}</span>
          {tool.badge && <span className="text-xs opacity-70">{tool.badge}</span>}
          {hasSubTypes && (
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
          )}
        </motion.button>

        {/* 서브타입 드롭다운 - 버튼 아래 왼쪽 정렬 */}
        <AnimatePresence>
          {hasSubTypes && isDropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: 4, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.96 }}
              transition={{ duration: 0.15, ease: [0.2, 0, 0.2, 1] }}
              className="absolute top-full left-0 mt-2 z-50
                bg-white dark:bg-gray-800 rounded-xl
                shadow-[0_4px_24px_-4px_rgba(0,0,0,0.12)] dark:shadow-[0_4px_24px_-4px_rgba(0,0,0,0.4)]
                border border-gray-200/80 dark:border-gray-700
                p-1.5 min-w-[200px]"
            >
              {/* 드롭다운 화살표 - 버튼 중앙에 맞춤 */}
              <div className="absolute -top-[6px] left-6 w-3 h-3 rotate-45
                bg-white dark:bg-gray-800 border-l border-t border-gray-200/80 dark:border-gray-700" />

              <div className="relative space-y-0.5">
                {tool.subTypes.map((subType, idx) => {
                  const isSubTypeSelected = selectedSubType?.id === subType.id;
                  const Icon = ICON_MAP[subType.icon] || ICON_MAP.Circle;
                  return (
                    <motion.button
                      key={subType.id}
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSubTypeSelect(subType);
                        setOpenDropdownId(null); // 선택 후 드롭다운 닫기
                      }}
                      className={`
                        w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm
                        transition-all duration-150
                        ${isSubTypeSelected
                          ? 'bg-sedaily-50 dark:bg-sedaily-900/30 text-sedaily-600 dark:text-sedaily-400'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }
                      `}
                    >
                      <div className={`
                        w-7 h-7 rounded-md flex items-center justify-center transition-colors duration-150
                        ${isSubTypeSelected
                          ? 'bg-sedaily-100 dark:bg-sedaily-800/50 text-sedaily-500'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                        }
                      `}>
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <span className={`flex-1 text-left font-medium ${isSubTypeSelected ? 'text-sedaily-600 dark:text-sedaily-400' : ''}`}>
                        {subType.name}
                      </span>
                      {isSubTypeSelected && (
                        <Check className="w-4 h-4 text-sedaily-500" />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <motion.div
      className="space-y-2.5 mt-5"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* 1열: 텍스트 도구 - 블루 계열 */}
      <div data-tutorial="text-tools" className="flex flex-wrap gap-2 justify-center">
        {TEXT_TOOL_LIST.map((tool, index) => renderToolButton(tool, index, 'blue', 0))}
      </div>

      {/* 2열: 시각화 + 오디오 도구 - 바이올렛 계열 */}
      <div data-tutorial="visual-tools" className="flex flex-wrap gap-2 justify-center">
        {VISUAL_TOOL_LIST.map((tool, index) => renderToolButton(tool, index, 'violet', 0.1))}
        {AUDIO_TOOL_LIST.map((tool, index) => renderToolButton(tool, VISUAL_TOOL_LIST.length + index, 'emerald', 0.1))}
      </div>
    </motion.div>
  );
};

// PRISM Audience Selector - 독자 유형 선택 토글
const PrismAudienceSelector = ({ tool, selectedAudiences, onAudienceToggle }) => {
  if (tool?.id !== 'prism' || !tool?.audienceTypes) return null;

  const allSelected = selectedAudiences.length === tool.audienceTypes.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onAudienceToggle([]); // 전체 해제
    } else {
      onAudienceToggle(tool.audienceTypes.map(a => a.id)); // 전체 선택
    }
  };

  const handleToggle = (audienceId) => {
    if (selectedAudiences.includes(audienceId)) {
      onAudienceToggle(selectedAudiences.filter(id => id !== audienceId));
    } else {
      onAudienceToggle([...selectedAudiences, audienceId]);
    }
  };

  return (
    <div className="w-full rounded-2xl bg-bg-200/40 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-text-100">독자 유형 선택</h3>
          <p className="text-sm text-text-400 mt-0.5">AI가 선택한 유형에 맞는 기사를 선별합니다</p>
        </div>
        <button
          onClick={handleSelectAll}
          className="text-sm px-4 py-2 rounded-full bg-bg-300/50 text-text-300 hover:bg-bg-300 transition-colors"
        >
          {allSelected ? '전체 해제' : '전체 선택'}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {tool.audienceTypes.map((audience) => {
          const isSelected = selectedAudiences.includes(audience.id);
          return (
            <button
              key={audience.id}
              onClick={() => handleToggle(audience.id)}
              className={`
                flex flex-col items-center gap-2 p-4 rounded-xl transition-all duration-200
                ${isSelected
                  ? 'bg-sky-500/15 border-2 border-sky-500/40 shadow-sm'
                  : 'bg-bg-300/30 border-2 border-transparent hover:bg-bg-300/50'
                }
              `}
            >
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center
                ${isSelected ? 'bg-sky-500/20' : 'bg-bg-300/50'}
              `}>
                <ToolIcon
                  name={audience.icon}
                  className={`w-6 h-6 ${isSelected ? 'text-sky-500' : 'text-text-400'}`}
                />
              </div>
              <div className="text-center">
                <div className={`text-sm font-medium ${isSelected ? 'text-sky-500' : 'text-text-200'}`}>
                  {audience.name}
                </div>
                <div className="text-xs text-text-500 mt-0.5">
                  {audience.description}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {selectedAudiences.length > 0 && (
        <div className="mt-4 pt-4 border-t border-bg-300/30 flex items-center justify-between">
          <p className="text-sm text-text-400">
            <span className="text-sky-500 font-semibold">{selectedAudiences.length}개</span> 유형 선택됨
          </p>
          <p className="text-xs text-text-500">선택한 유형에 맞는 기사를 입력하면 AI가 분석합니다</p>
        </div>
      )}
    </div>
  );
};

// Tool Guide Card - Toss Style Minimal Design with Animation
const ToolGuideCard = ({ tool, selectedAudiences, onAudienceToggle }) => {
  if (!tool?.guide) return null;

  const { guide } = tool;
  const isPrism = tool.id === 'prism';

  // PRISM 전체 선택/해제
  const allSelected = isPrism && selectedAudiences?.length === tool.audienceTypes?.length;
  const handleSelectAll = () => {
    if (allSelected) {
      onAudienceToggle?.([]);
    } else {
      onAudienceToggle?.(tool.audienceTypes?.map(a => a.id) || []);
    }
  };
  const handleToggle = (audienceId) => {
    if (selectedAudiences?.includes(audienceId)) {
      onAudienceToggle?.(selectedAudiences.filter(id => id !== audienceId));
    } else {
      onAudienceToggle?.([...(selectedAudiences || []), audienceId]);
    }
  };

  return (
    <motion.div
      className="rounded-2xl bg-bg-200/40 p-5 mb-5"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      {/* 헤더 - 심플하게 */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-text-100">
          {guide.title}
        </h3>
        {isPrism && (
          <button
            onClick={handleSelectAll}
            className="text-xs px-3 py-1.5 rounded-full bg-bg-300/50 text-text-300 hover:bg-bg-300 transition-colors"
          >
            {allSelected ? '전체 해제' : '전체 선택'}
          </button>
        )}
      </div>

      {/* PRISM: 독자 유형 선택 그리드 */}
      {isPrism && tool.audienceTypes && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {tool.audienceTypes.map((audience) => {
            const isSelected = selectedAudiences?.includes(audience.id);
            return (
              <motion.button
                key={audience.id}
                onClick={() => handleToggle(audience.id)}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`
                  flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all duration-200
                  ${isSelected
                    ? 'bg-sky-500/15 border border-sky-500/40'
                    : 'bg-bg-300/30 border border-transparent hover:bg-bg-300/50'
                  }
                `}
              >
                <div className={`
                  w-9 h-9 rounded-lg flex items-center justify-center
                  ${isSelected ? 'bg-sky-500/20' : 'bg-bg-300/50'}
                `}>
                  <ToolIcon
                    name={audience.icon}
                    className={`w-5 h-5 ${isSelected ? 'text-sky-500' : 'text-text-400'}`}
                  />
                </div>
                <span className={`text-xs font-medium ${isSelected ? 'text-sky-500' : 'text-text-300'}`}>
                  {audience.name}
                </span>
              </motion.button>
            );
          })}
        </div>
      )}

      {/* 팁 목록 - Toss 스타일 */}
      {!isPrism && (
        <div className="space-y-3">
          <p className="text-xs text-text-500 font-medium tracking-wide">Try these examples</p>
          {guide.tips.map((tip, i) => (
            <motion.div
              key={i}
              className="flex items-baseline gap-3"
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: i * 0.05 }}
            >
              <span className="text-[11px] text-text-500 min-w-[60px] shrink-0">{tip.label}</span>
              <span className="text-[13px] text-text-200 leading-relaxed">"{tip.example}"</span>
            </motion.div>
          ))}
        </div>
      )}

      {/* 주의사항/선택된 유형 수 */}
      {(guide.notice || (isPrism && selectedAudiences?.length > 0)) && (
        <motion.div
          className="mt-4 pt-4 border-t border-bg-300/30"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          {isPrism && selectedAudiences?.length > 0 ? (
            <p className="text-xs text-text-400">
              <span className="text-sky-500 font-semibold">{selectedAudiences.length}</span> type(s) selected · {guide.notice}
            </p>
          ) : (
            <p className="text-xs text-text-500 leading-relaxed">
              {guide.notice}
            </p>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

// Tool Examples - Gemini Style with Animation
const ToolExamples = ({ tool, onExampleClick, selectedExample }) => {
  if (!tool) return null;

  // 가이드가 있는 도구는 예제 대신 가이드 카드에서 처리
  if (tool.guide) return null;

  return (
    <motion.div
      className="pt-4 space-y-1"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      {tool.examples.map((example, i) => {
        const isSelected = selectedExample === example;
        return (
          <motion.button
            key={i}
            onClick={() => onExampleClick(example)}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
            transition={{ duration: 0.2, delay: i * 0.05 }}
            className={`block w-full text-left text-sm px-3 py-2 rounded-lg transition-colors ${
              isSelected
                ? 'bg-purple-500/20 text-purple-300 ring-1 ring-purple-500/30'
                : 'text-text-400 hover:text-text-100 hover:bg-bg-200/30'
            }`}
          >
            {isSelected && <Check className="w-3.5 h-3.5 inline mr-2 text-purple-400" />}
            {example}
          </motion.button>
        );
      })}
    </motion.div>
  );
};

// Visual Template Gallery - Gemini Style (Full Screen)
const VisualTemplateGallery = ({ toolId, tool, onTemplateSelect, onClose }) => {
  const templateData = getTemplatesForTool(toolId);
  if (!templateData) return null;

  return (
    <div className="w-full">
      {/* Gallery Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-text-100">{templateData.title}</h2>
        <button
          onClick={onClose}
          className="p-2.5 rounded-full hover:bg-bg-300/50 text-text-400 hover:text-text-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Guide Card for image/infographic */}
      {tool?.guide && (
        <ToolGuideCard tool={tool} />
      )}

      {/* Template Cards Grid - Professional Design */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {templateData.templates.map((template) => (
          <button
            key={template.id}
            onClick={() => onTemplateSelect(template)}
            className="group relative aspect-[4/3] overflow-hidden rounded-xl transition-all duration-300 hover:scale-[1.02] shadow-lg hover:shadow-2xl border border-white/10 hover:border-white/20"
          >
            {/* Full Gradient Background */}
            <div className={`absolute inset-0 bg-gradient-to-br ${template.gradient} transition-all duration-300 group-hover:saturate-110`} />

            {/* Subtle Glass Effect Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20" />

            {/* Center Icon Container */}
            <div className="absolute inset-0 flex items-center justify-center pb-8">
              <div className="relative">
                {/* Icon Glow Effect */}
                <div className="absolute inset-0 blur-xl opacity-50 group-hover:opacity-70 transition-opacity">
                  <ToolIcon name={template.icon} className="w-14 h-14 text-white" />
                </div>
                {/* Main Icon */}
                <div className="relative w-14 h-14 text-white drop-shadow-md group-hover:scale-110 transition-transform duration-300">
                  <ToolIcon name={template.icon} className="w-full h-full stroke-[1.5]" />
                </div>
              </div>
            </div>

            {/* Bottom Label with Glass Effect */}
            <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 via-black/40 to-transparent">
              <h4 className="font-semibold text-white text-sm tracking-tight">
                {template.name}
              </h4>
              <p className="text-[11px] text-white/60 mt-0.5 line-clamp-1">
                {template.description}
              </p>
            </div>

            {/* Hover Indicator */}
            <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-200 transform translate-x-2 group-hover:translate-x-0">
              <div className="w-7 h-7 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/20">
                <Play className="w-3 h-3 text-white fill-white ml-0.5" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Quick Examples - Refined Design */}
      <div className="mt-6 p-4 bg-bg-200/30 rounded-xl border border-bg-300/30">
        <p className="text-xs text-text-500 mb-3 font-medium uppercase tracking-wider">추천 프롬프트</p>
        <div className="flex flex-wrap gap-2">
          {templateData.templates.flatMap(t => t.examples.slice(0, 1)).slice(0, 4).map((example, i) => (
            <button
              key={i}
              onClick={() => onTemplateSelect({ prompt: example })}
              className="text-sm text-text-300 hover:text-text-100 px-3.5 py-2 rounded-lg bg-bg-300/30 hover:bg-bg-300/50 transition-all duration-200 border border-bg-300/20 hover:border-bg-300/40"
            >
              {example}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Selected Tool Tag Component
const SelectedToolTag = ({ tool, subType, onClear }) => {
  const colorClasses = {
    sedaily: 'bg-sedaily-500/15 text-sedaily-400 border-sedaily-500/30',
    blue: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    emerald: 'bg-emerald-500/10 text-emerald-400',
    amber: 'bg-amber-500/10 text-amber-400',
    rose: 'bg-rose-500/10 text-rose-400',
    pink: 'bg-pink-500/10 text-pink-400',
    cyan: 'bg-cyan-500/10 text-cyan-400',
    violet: 'bg-violet-500/10 text-violet-400',
    orange: 'bg-orange-500/10 text-orange-400',
    fuchsia: 'bg-fuchsia-500/10 text-fuchsia-400',
    teal: 'bg-teal-500/10 text-teal-400',
    lime: 'bg-lime-500/10 text-lime-400',
  };

  // SubType이 있으면 SubType 이름 표시, 없으면 도구 이름 표시
  const displayName = subType ? subType.name : tool.name;
  const displayIcon = subType?.icon || tool.icon;

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium
        ${colorClasses[tool.color] || colorClasses.sedaily}
      `}
    >
      <ToolIcon name={displayIcon} className="w-3.5 h-3.5" />
      <span>{displayName}</span>
      {!subType && tool.badge && <span>{tool.badge}</span>}
      <button
        onClick={onClear}
        className="ml-0.5 p-0.5 rounded hover:bg-white/10 transition-colors"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
};

// Selected Example Tag Component - 선택된 샘플 질문 태그
const SelectedExampleTag = ({ example, onClear }) => {
  // 긴 텍스트는 줄임 처리
  const displayText = example.length > 20 ? example.substring(0, 20) + '...' : example;

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium bg-purple-500/15 text-purple-400 max-w-[200px]"
    >
      <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="truncate">{displayText}</span>
      <button
        onClick={onClear}
        className="ml-0.5 p-0.5 rounded hover:bg-white/10 transition-colors flex-shrink-0"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
};

// Chat input component - Gemini Style
const ChatInput = ({ value, onChange, onSubmit, isProcessing, attachedFile, onFileAttach, onFileRemove, selectedTool, selectedSubType, onToolSelect, onToolClear, selectedModel, onModelSelect, selectedExample, onExampleClear, isInitialScreen = false }) => {
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const toolMenuRef = useRef(null);
  const modelMenuRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showToolMenu, setShowToolMenu] = useState(false);
  const [showModelMenu, setShowModelMenu] = useState(false);

  // 도구 메뉴 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (toolMenuRef.current && !toolMenuRef.current.contains(event.target)) {
        setShowToolMenu(false);
      }
    };
    if (showToolMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showToolMenu]);

  // 모델 메뉴 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modelMenuRef.current && !modelMenuRef.current.contains(event.target)) {
        setShowModelMenu(false);
      }
    };
    if (showModelMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showModelMenu]);

  // ESC 키로 모든 메뉴 닫기
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowToolMenu(false);
        setShowModelMenu(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + "px";
    }
  }, [value]);

  const handleKeyDown = (e) => {
    // IME 조합 중에는 Enter 무시 (한글 입력 중복 방지)
    if (e.nativeEvent.isComposing || e.keyCode === 229) {
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isProcessing && (value.trim() || selectedExample)) {
        onSubmit();
      }
    }
  };

  // 파일 선택 핸들러
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await processFile(file);
    }
    e.target.value = '';
  };

  // 드래그앤드롭 핸들러
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  // 파일 처리
  const processFile = async (file) => {
    const fileName = file.name.toLowerCase();
    const isSpreadsheet = fileName.endsWith('.csv') ||
                          fileName.endsWith('.xlsx') ||
                          fileName.endsWith('.xls') ||
                          fileName.endsWith('.txt');

    if (!isSpreadsheet) {
      toast.error('CSV 또는 Excel 파일만 지원합니다');
      return;
    }

    try {
      const tableData = await parseSpreadsheetFile(file);
      if (tableData.rows.length === 0) {
        toast.error('데이터가 없는 파일입니다');
        return;
      }
      // 파일을 첨부 상태로 저장 (바로 채팅에 추가하지 않음)
      onFileAttach({ tableData, fileName: file.name });
      toast.success('파일이 첨부되었습니다. 원하는 작업을 입력하세요.');
    } catch (error) {
      console.error('File parse error:', error);
      toast.error(error.message || '파일을 읽는 중 오류가 발생했습니다');
    }
  };

  return (
    <div className={`${isInitialScreen ? '' : 'sticky bottom-0 bg-bg-100 pt-4 pb-8'} safe-area-bottom`}>
      <div className={`${isInitialScreen ? '' : 'max-w-4xl mx-auto px-4'}`}>
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            flex flex-col rounded-2xl relative w-full z-[2]
            ${isInitialScreen
              ? 'bg-bg-200 border border-bg-300/50'
              : 'bg-bg-200/80'
            }
            transition-all duration-200 shadow-soft-lg
            ${isDragOver ? "ring-2 ring-sedaily-500 bg-sedaily-500/5" : ""}
            ${isProcessing ? "" : "focus-within:ring-1 focus-within:ring-sedaily-500/30"}
          `}
        >
          {isDragOver && (
            <div className="absolute inset-0 flex items-center justify-center bg-sedaily-500/5 rounded-2xl z-10 pointer-events-none">
              <div className="flex items-center gap-2 text-sedaily-500">
                <Upload className="w-5 h-5" />
                <span className="text-sm font-medium">파일을 놓으세요</span>
              </div>
            </div>
          )}

          {/* Attached File Preview */}
          {attachedFile && (
            <div className="px-4 pt-3">
              <div className="flex items-center gap-2 p-2.5 bg-bg-300/40 rounded-lg">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Table className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-100 truncate">{attachedFile.fileName}</p>
                  <p className="text-xs text-text-400">
                    {attachedFile.tableData.headers.length}열 × {attachedFile.tableData.rows.length}행
                  </p>
                </div>
                <button
                  onClick={onFileRemove}
                  className="p-1.5 rounded-lg text-text-400 hover:text-text-200 hover:bg-bg-300/60 transition-colors"
                  title="첨부 파일 제거"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Text Input Area */}
          <div className="px-4 pt-4 pb-2">
            <textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={attachedFile ? "What would you like to do with this data? (e.g., sort by revenue)" : (selectedTool ? selectedTool.placeholder : "How can I help you today? Select a tool below.")}
              disabled={isProcessing}
              rows={2}
              className="
                w-full bg-transparent border-none
                text-text-100 placeholder-text-400
                resize-none focus:outline-none
                min-h-[48px] max-h-[160px] overflow-y-auto
                text-sm leading-relaxed
                disabled:opacity-50
              "
            />
          </div>

          {/* Bottom Toolbar */}
          <div className="flex items-center justify-between px-3 py-2 border-t border-bg-300/30">
            {/* Left: Tools */}
            <div className="flex items-center gap-1">
              {/* Attach Button */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.txt,.xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
                className="p-2 rounded-lg text-text-400 hover:text-text-200 hover:bg-bg-300/60 transition-colors disabled:opacity-40"
                title="파일 첨부"
              >
                <Paperclip className="w-4 h-4" />
              </button>

              {/* Selected Tool Tag - SubType이 있으면 SubType 표시 */}
              {selectedTool && (
                <SelectedToolTag tool={selectedTool} subType={selectedSubType} onClear={onToolClear} />
              )}

              {/* Selected Example Tag - 선택된 샘플 질문 */}
              {selectedExample && (
                <SelectedExampleTag example={selectedExample} onClear={onExampleClear} />
              )}
            </div>

            {/* Right: Model Selector + Send Button */}
            <div className="flex items-center gap-2">
              {/* Model Selector */}
              <div className="relative" ref={modelMenuRef}>
                {/* 도구에 연결된 모델이 있으면 비활성화 표시 */}
                {selectedTool?.linkedModelId ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-yellow-500/10 text-yellow-500 border border-yellow-500/20">
                    <span className="text-sm">{selectedModel?.icon}</span>
                    <span className="text-xs font-medium">{selectedModel?.name}</span>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowModelMenu(!showModelMenu)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-text-400 hover:text-text-200 hover:bg-bg-300/60 transition-colors"
                  >
                    <ModelIcon providerId={selectedModel?.providerId || 'anthropic'} modelId={selectedModel?.id} className="w-4 h-4" />
                    <span className="text-xs hidden sm:inline">{selectedModel?.name || 'Claude'}</span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${showModelMenu ? 'rotate-180' : ''}`} />
                  </button>
                )}

                {/* Model Dropdown - 도구에 연결된 모델이 없을 때만 표시 */}
                {/* 메인화면에서는 아래로, 채팅화면에서는 위로 열림 */}
                {showModelMenu && !selectedTool?.linkedModelId && (
                  <div className={`absolute left-0 bg-bg-100 rounded-xl shadow-soft-xl overflow-hidden min-w-[220px] max-h-[320px] overflow-y-auto border border-bg-300/30 z-50 ${
                    isInitialScreen ? 'top-full mt-2' : 'bottom-full mb-2'
                  }`}>
                    <div className="p-1.5">
                      <div className="px-2 py-1.5 text-xs text-text-500 font-medium">Select AI model</div>
                      {AI_MODELS.filter(m => !m.hidden).map((model) => (
                        <button
                          key={model.id}
                          onClick={() => {
                            onModelSelect(model);
                            setShowModelMenu(false);
                          }}
                          className={`
                            w-full flex items-center gap-2.5 px-2.5 py-2 text-sm text-left rounded-lg
                            transition-colors
                            ${selectedModel?.id === model.id
                              ? `${model.bgColor} ${model.textColor}`
                              : 'text-text-300 hover:bg-bg-200 hover:text-text-100'
                            }
                          `}
                        >
                          <ModelIcon providerId={model.providerId} modelId={model.id} className="w-4 h-4" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium">{model.name}</div>
                            <div className="text-xs text-text-500 truncate">{model.description}</div>
                          </div>
                          {selectedModel?.id === model.id && (
                            <Check className="w-4 h-4 flex-shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Send Button */}
              <button
              onClick={onSubmit}
              disabled={isProcessing || (!value.trim() && !selectedExample)}
              className={`
                flex items-center justify-center w-9 h-9 rounded-xl
                transition-all duration-200
                ${isProcessing || (!value.trim() && !selectedExample)
                  ? "bg-bg-300/50 text-text-500 cursor-not-allowed"
                  : "bg-sedaily-500 text-white hover:bg-sedaily-600 active:scale-95"
                }
              `}
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
            </div>
          </div>
        </div>

        {/* Tool Buttons Below Input (when no tool selected and no messages) */}
      </div>
    </div>
  );
};

// Demo mode fallback responses
const getDemoResponse = (content, tool) => {
  if (tool === "table" || content.includes("표")) {
    return {
      content: "요청하신 표를 생성했습니다.",
      table: {
        headers: ["기업", "시가총액", "전년대비"],
        rows: [
          ["Apple", "$3.0T", "+15%"],
          ["Microsoft", "$2.9T", "+22%"],
          ["Google", "$1.9T", "+18%"],
          ["Samsung", "$350B", "+5%"],
        ],
      },
    };
  } else if (tool === "search" || content.includes("search")) {
    return {
      content: `Here are the search results for "${content}".\n\n1. **Latest AI Trends 2024**\n   - GPT-4o multimodal capabilities enhanced\n   - Claude 3.5 Sonnet released\n   - Open-source LLM competition intensifies\n\n2. **Top News**\n   - OpenAI announces new reasoning model\n   - Google set to release Gemini 2.0`,
    };
  } else if (tool === "document" || content.includes("proposal") || content.includes("document")) {
    return {
      content: `## Draft Proposal\n\n### 1. Overview\nThis proposal outlines an AI-powered news analysis service.\n\n### 2. Objectives\n- Automate real-time news analysis\n- Improve journalist productivity\n- Support high-quality content production\n\n### 3. Expected Outcomes\n30% reduction in work time, improved content quality`,
    };
  }
  return {
    content: `Hello! I'm here to help with "${content}".\n\nUse the tool buttons below to access specific features:\n- **Table** - Organize data into tables\n- **Document** - Write proposals and reports\n- **Search** - Real-time web search\n- **Voice** - Text-to-speech conversion`,
  };
};

// Share Modal Component
const ShareModal = ({ isOpen, onClose, data }) => {
  const [title, setTitle] = useState(data?.title || "");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const { addWork } = useTeamStore();

  useEffect(() => {
    if (data) {
      setTitle(data.content?.substring(0, 30) || "New Result");
    }
  }, [data]);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleShare = async () => {
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    const newWork = {
      title: title.trim(),
      description: description.trim(),
      content: data.content,
      workType: data.tool || "table",
      table: data.table,
      tags,
    };

    addWork({
      ...newWork,
      id: `work_${Date.now()}`,
      userId: "user_current",
      userName: "Me",
      department: "IT",
      likes: 0,
      likedBy: [],
      commentCount: 0,
      comments: [],
      createdAt: new Date().toISOString(),
    });

    toast.success("Shared to team library");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
      <div className="bg-bg-100 rounded-2xl w-full max-w-md shadow-soft-xl">
        <div className="p-6">
          <h2 className="text-lg font-bold text-text-100 mb-4">Share with Team</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-300 mb-1.5">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Result title"
                className="w-full bg-bg-200 text-text-100 text-sm px-3 py-2.5 rounded-lg outline-none focus:ring-1 focus:ring-sedaily-500/30 transition-all shadow-inset-soft"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-300 mb-1.5">Description (optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add a brief description"
                rows={3}
                className="w-full bg-bg-200 text-text-100 text-sm px-3 py-2.5 rounded-lg outline-none focus:ring-1 focus:ring-sedaily-500/30 resize-none transition-all shadow-inset-soft"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-300 mb-1">Tags (optional)</label>
              <div className="flex gap-2 mb-2 flex-wrap">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="flex items-center gap-1 bg-sedaily-500/15 text-sedaily-400 text-xs px-2 py-1 rounded-md"
                  >
                    #{tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-sedaily-300 ml-0.5"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                    if (e.key === "Enter") { e.preventDefault(); handleAddTag(); }
                  }}
                  placeholder="Add a tag"
                  className="flex-1 bg-bg-200 text-text-100 text-sm px-3 py-2 rounded-lg outline-none focus:ring-1 focus:ring-sedaily-500/30 transition-all shadow-inset-soft"
                />
                <button
                  onClick={handleAddTag}
                  className="px-3 py-2 bg-bg-200 text-text-300 text-sm rounded-lg hover:bg-bg-300 hover:text-text-100 transition-colors shadow-soft"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-4 border-t border-bg-300/50">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-text-300 hover:text-text-100 hover:bg-bg-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleShare}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-sedaily-500 hover:bg-sedaily-600 rounded-lg transition-colors"
          >
            Share
          </button>
        </div>
      </div>
    </div>
  );
};

// Chat View Component
const ChatView = () => {
  const navigate = useNavigate();
  const { id: conversationId } = useParams();
  const { useDemo } = useOutletContext() || { useDemo: !isBedrockConfigured() };

  const {
    messages,
    setMessages,
    addMessage,
    updateMessage,
    currentConversationId,
    setCurrentConversation,
    selectedTool,
    setSelectedTool,
    selectedSubType,
    setSelectedSubType,
    selectedModel,
    setSelectedModel,
    isProcessing,
    setProcessing,
    isLoadingConversation,
    setLoadingConversation,
    resetChat,
    newChatRequested,
    clearNewChatRequest,
    // 팟캐스트 퍼널
    podcastFunnel,
    startPodcastFunnel,
    resetPodcastFunnel,
  } = useChatStore();

  const { conversations, addConversation, updateConversationMessages } = useSidebarStore();
  const { openShareModal, isModalOpen, closeShareModal, shareData } = useTeamStore();

  const [inputValue, setInputValue] = useState("");
  const [selectedExample, setSelectedExample] = useState(null); // 선택된 샘플 질문
  const [attachedFile, setAttachedFile] = useState(null); // { tableData, fileName }
  const [isAnimatingToChat, setIsAnimatingToChat] = useState(false); // 🎬 메인→채팅 전환 애니메이션
  const [selectedAudiences, setSelectedAudiences] = useState([]); // PRISM 독자 유형 선택
  const [showChartConverter, setShowChartConverter] = useState(false); // 차트 변환 모달
  const [showChartSlicer, setShowChartSlicer] = useState(false); // 차트 자르기 모달
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null); // 스크롤 컨테이너 ref
  const [userScrolledUp, setUserScrolledUp] = useState(false); // 사용자가 위로 스크롤했는지
  const currentAssistantId = useRef(null);

  // 🔧 즉시 스트리밍 (효과 없이 최대 속도)
  const {
    displayText: streamingText,
    isStreaming: isTextStreaming,
    appendText: appendStreamingText,
    finish: finishStreaming,
    reset: resetStreaming,
  } = useSmoothStreaming();

  // Load conversation if ID provided
  useEffect(() => {
    if (conversationId && conversationId !== currentConversationId) {
      const latestConversations = useSidebarStore.getState().conversations;
      const localConv = latestConversations.find(c => c.id === conversationId);

      // 🎬 최소 스켈레톤 표시 시간 (500ms) - 빠르게 전환
      const MIN_SKELETON_TIME = 500;
      console.log('%c[SKELETON] 🎬 스켈레톤 시작!', 'color: #f59e0b; font-weight: bold;');
      const startTime = Date.now();

      // Cache hit → load after min skeleton time
      if (localConv?.messages?.length > 0) {
        setCurrentConversation(conversationId);
        // Force isStreaming=false on all messages (in case any were saved mid-stream)
        const cleanMessages = localConv.messages.map(m =>
          m.isStreaming ? { ...m, isStreaming: false } : m
        );
        setMessages(cleanMessages);

        // 최소 시간 보장 후 스켈레톤 숨기기
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, MIN_SKELETON_TIME - elapsed);
        console.log('%c[SKELETON] ⏱️ 대기 시간:', 'color: #f59e0b;', remaining + 'ms');
        setTimeout(() => {
          console.log('%c[SKELETON] ✅ 스켈레톤 종료!', 'color: #22c55e; font-weight: bold;');
          setLoadingConversation(false);
        }, remaining);
        return;
      }

      // 캐시 미스 → API 호출 (최소 스켈레톤 시간 보장)
      const loadConversation = async () => {
        try {
          const conv = await conversationService.getConversationById(conversationId);
          if (conv) {
            setCurrentConversation(conversationId);
            const cleanMessages = (conv.messages || []).map(m =>
              m.isStreaming ? { ...m, isStreaming: false } : m
            );
            setMessages(cleanMessages);
            if (cleanMessages.length > 0) {
              updateConversationMessages(conversationId, cleanMessages);
            }
          } else {
            navigate("/");
          }
        } catch (error) {
          console.error('[CHAT] 대화 로드 실패:', error);
          navigate("/");
        } finally {
          // 최소 스켈레톤 시간 보장
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, MIN_SKELETON_TIME - elapsed);
          setTimeout(() => {
            setLoadingConversation(false);
          }, remaining);
        }
      };
      loadConversation();
    } else if (!conversationId && currentConversationId) {
      resetChat();
    }
  }, [conversationId, currentConversationId, setCurrentConversation, setMessages, navigate, resetChat, updateConversationMessages, setLoadingConversation]);

  // Reset local state when new chat is explicitly requested
  useEffect(() => {
    if (newChatRequested) {
      console.log('%c[CHAT] 🧹 로컬 상태 초기화', 'color: #22c55e; font-weight: bold;');
      setAttachedFile(null);
      setInputValue('');
      resetStreaming();
      clearNewChatRequest();
    }
  }, [newChatRequested, clearNewChatRequest, resetStreaming]);

  // Auto scroll - 새 메시지 전송 시 & 스트리밍 시작 시 자동 스크롤
  const scrollToBottom = useCallback((behavior = "smooth") => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior });
    }, 50);
  }, []);

  // 대화 로드 시 최하단으로 스크롤 (하단 고정 레이아웃이라 불필요하지만 안전장치)
  useEffect(() => {
    if (messages.length > 0 && !isLoadingConversation) {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
    }
  }, [currentConversationId]);

  // 스트리밍 중 자동 스크롤 (throttled) - 사용자가 위로 스크롤하지 않은 경우에만
  const lastScrollTime = useRef(0);
  useEffect(() => {
    if (streamingText && isProcessing && !userScrolledUp) {
      const now = Date.now();
      if (now - lastScrollTime.current > 300) { // 300ms 간격
        lastScrollTime.current = now;
        scrollToBottom("smooth");
      }
    }
  }, [streamingText, isProcessing, userScrolledUp]);

  // 사용자 스크롤 감지 - 위로 스크롤하면 자동 스크롤 멈춤
  const handleChatScroll = useCallback((e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100; // 하단 100px 이내

    if (isAtBottom) {
      setUserScrolledUp(false); // 하단에 있으면 자동 스크롤 재개
    } else if (isProcessing) {
      setUserScrolledUp(true); // 스트리밍 중 위로 스크롤하면 자동 스크롤 멈춤
    }
  }, [isProcessing]);

  // 새 메시지 전송 시 자동 스크롤 플래그 리셋
  useEffect(() => {
    if (!isProcessing) {
      setUserScrolledUp(false);
    }
  }, [isProcessing]);

  // Prewarm WebSocket connection for low latency
  useEffect(() => {
    if (!useDemo) {
      prewarmConnection();
    }
  }, [useDemo]);

  const handleSubmit = useCallback(async (e, directMessage = null) => {
    // 선택된 샘플 질문이 있으면 입력값 앞에 추가
    const baseMessage = directMessage || inputValue.trim();
    const messageToSend = selectedExample
      ? (baseMessage ? `${selectedExample}\n\n${baseMessage}` : selectedExample)
      : baseMessage;

    if (!messageToSend || isProcessing) return;

    // ============================================
    // 🔍 DEBUG: handleSubmit 시작
    // ============================================
    console.group('%c📤 handleSubmit 실행', 'color: #8b5cf6; font-weight: bold; font-size: 14px;');
    console.log('%c[UI] 현재 선택된 도구:', 'color: #f59e0b; font-weight: bold;', selectedTool || '없음');
    console.log('%c[UI] 선택된 모델:', 'color: #f59e0b; font-weight: bold;', selectedModel?.displayName || 'default');
    console.log('%c[UI] 첨부 파일:', 'color: #f59e0b; font-weight: bold;', attachedFile ? attachedFile.fileName : '없음');
    console.log('%c[UI] 선택된 샘플:', 'color: #a855f7; font-weight: bold;', selectedExample || '없음');
    console.log('%c[UI] 입력 메시지:', 'color: #3b82f6;', messageToSend.substring(0, 100) + (messageToSend.length > 100 ? '...' : ''));

    // 첨부 파일이 있으면 표 데이터를 프롬프트에 포함
    let fullContent = messageToSend;
    let attachedTableData = null;

    if (attachedFile) {
      if (attachedFile.type === 'image') {
        // 이미지 첨부 - 텍스트 프롬프트에 파일명만 표시
        fullContent = `[첨부 이미지: ${attachedFile.fileName}]\n\n요청: ${messageToSend}`;
      } else {
        const { tableData, fileName } = attachedFile;
        attachedTableData = tableData;

        // 표 데이터를 텍스트로 변환하여 프롬프트에 추가
        const tableText = [
          `[첨부 파일: ${fileName}]`,
          `열: ${tableData.headers.join(', ')}`,
          '',
          tableData.headers.join('\t'),
          ...tableData.rows.map(row => row.join('\t'))
        ].join('\n');

        fullContent = `${tableText}\n\n---\n\n요청: ${messageToSend}`;
        console.log('%c[UI] ⚠️ 첨부 파일로 인해 fullContent 수정됨', 'color: #f59e0b;');
      }
    }

    const userMessage = {
      id: `msg_${Date.now()}`,
      role: "user",
      content: messageToSend, // 화면에 표시될 내용
      attachedFile: attachedFile, // 첨부 파일 정보
      tool: selectedTool || (attachedFile ? 'table' : null),
      timestamp: new Date().toISOString(),
    };
    console.log('%c[UI] userMessage.tool:', 'color: #22c55e; font-weight: bold;', userMessage.tool || '없음');

    // Determine conversation ID
    let convId = currentConversationId;
    let isNewConversation = false;
    if (!convId) {
      convId = `conv_${Date.now()}`;
      isNewConversation = true;
      setCurrentConversation(convId);
    }

    const effectiveTool = selectedTool || (attachedFile ? 'table' : null);

    // 🔍 DEBUG: effectiveTool 결정
    console.log('%c[TOOL] effectiveTool 결정됨:', 'color: #ec4899; font-weight: bold; font-size: 12px;', effectiveTool || 'null (default 사용됨)');

    // Add streaming placeholder
    const assistantId = `msg_${Date.now() + 1}`;
    currentAssistantId.current = assistantId;
    const assistantMessage = { id: assistantId, role: "assistant", content: "", isStreaming: true, tool: effectiveTool };

    // 🔧 새 대화인 경우: sidebar에 messages 포함하여 저장 (404 방지)
    if (isNewConversation) {
      // 제목: messageToSend 사용 (샘플 질문 포함)
      const titleSource = messageToSend || inputValue.trim();
      const newConv = {
        id: convId,
        title: titleSource.substring(0, 30) + (titleSource.length > 30 ? "..." : ""),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastTool: selectedTool,
        messageCount: 2,
        messages: [userMessage, assistantMessage], // 🔧 메시지 포함!
      };
      addConversation(newConv);
      console.log('%c[NAV] 🚀 새 대화 생성됨 (messages 포함):', 'color: #22c55e; font-weight: bold;', newConv.id);
    }

    // chatStore에도 메시지 추가
    addMessage(userMessage);
    addMessage(assistantMessage);
    setInputValue("");
    setSelectedExample(null); // 선택된 샘플 질문 초기화
    setAttachedFile(null);
    setProcessing(true);

    // 🔧 새 메시지 전송 시 자동 스크롤
    scrollToBottom();

    // 🎬 애니메이션 + navigate (새 대화인 경우)
    if (isNewConversation) {
      // 1. 애니메이션 시작
      setIsAnimatingToChat(true);
      console.log('%c[NAV] 🎬 애니메이션 시작', 'color: #22c55e; font-weight: bold;');

      // 2. 280ms 후 실제 navigate (페이드 애니메이션 완료 대기)
      setTimeout(() => {
        console.log('%c[NAV] ✅ navigate 실행!', 'color: #22c55e; font-weight: bold;', `/chat/${convId}`);
        navigate(`/chat/${convId}`, { replace: true });
        setIsAnimatingToChat(false);
      }, 280);
    }

    // Check if tool uses nanobanana model (direct Gemini image generation)
    const currentToolConfig = effectiveTool ? getToolById(effectiveTool) : null;
    const useDirectGemini = currentToolConfig?.linkedModelId === 'nanobanana';

    if (useDemo) {
      // Demo mode
      console.log('%c[DEMO] 데모 모드 사용 중', 'color: #f59e0b; font-weight: bold;');
      console.groupEnd(); // handleSubmit 그룹 종료
      setTimeout(() => {
        const demoResult = getDemoResponse(fullContent, effectiveTool);
        updateMessage(assistantId, {
          ...demoResult,
          isStreaming: false,
          tool: effectiveTool,
          timestamp: new Date().toISOString(),
        });
        setProcessing(false);
        setSelectedTool(null);

        // Update local state (demo mode - no backend save)
        const updatedMessages = [
          ...messages,
          userMessage,
          { id: assistantId, role: "assistant", ...demoResult, tool: effectiveTool, timestamp: new Date().toISOString() },
        ];
        updateConversationMessages(convId, updatedMessages, true);
      }, 1500);
    } else if (useDirectGemini) {
      // 🍌 Gemini 직접 이미지 생성 (infographic/image tools)
      console.log('%c[GEMINI] 🍌 나노바나나 이미지 생성 모드', 'color: #f59e0b; font-weight: bold;');
      console.groupEnd();

      try {
        // Check if Gemini is configured
        if (!geminiImageService.isConfigured()) {
          throw new Error('Gemini API 키가 설정되지 않았습니다. .env 파일에 VITE_GEMINI_API_KEY를 설정해주세요.');
        }

        // Update message to show generating status
        updateMessage(assistantId, {
          content: '🍌 이미지를 생성하고 있습니다...',
          isStreaming: true,
          tool: effectiveTool,
        });

        let result;
        if (effectiveTool === 'infographic') {
          // Infographic generation
          console.log('%c[GEMINI] 인포그래픽 생성 중...', 'color: #f59e0b;');
          result = await geminiImageService.generateInfographic(
            inputValue.trim(),
            { style: 'modern' }
          );
        } else {
          // Image generation
          console.log('%c[GEMINI] 이미지 생성 중...', 'color: #f59e0b;');
          result = await geminiImageService.generateImage(
            inputValue.trim(),
            { style: 'photorealistic' }
          );
        }

        console.log('%c[GEMINI] 생성 완료!', 'color: #22c55e; font-weight: bold;', result);

        // Prepare visualData with generated image
        const visualData = {
          type: effectiveTool,
          data: {
            title: inputValue.trim().substring(0, 50),
            prompt: inputValue.trim(),
            style: effectiveTool === 'infographic' ? 'modern' : 'photorealistic',
          },
          generatedImage: result.url ? {
            url: result.url,
            description: result.description || inputValue.trim(),
          } : null,
          imageError: result.error || null,
        };

        // Final response content
        const responseContent = result.url
          ? `🍌 ${effectiveTool === 'infographic' ? '인포그래픽' : '이미지'}가 생성되었습니다!\n\n${result.description || ''}`
          : `이미지 생성에 실패했습니다: ${result.error || '알 수 없는 오류'}`;

        // Update final state
        updateMessage(assistantId, {
          content: responseContent,
          visualData: visualData,
          isStreaming: false,
          tool: effectiveTool,
          timestamp: new Date().toISOString(),
        });
        setProcessing(false);
        setSelectedTool(null);

        // Update conversation
        const updatedMessages = [
          ...messages,
          userMessage,
          {
            id: assistantId,
            role: "assistant",
            content: responseContent,
            visualData: visualData,
            tool: effectiveTool,
            timestamp: new Date().toISOString(),
          },
        ];
        updateConversationMessages(convId, updatedMessages, true);
        // 백엔드 저장 완료 후 사이드바 목록 갱신
        window.dispatchEvent(new CustomEvent('conversationSaved'));

      } catch (error) {
        console.log('%c[GEMINI] ❌ 이미지 생성 실패!', 'color: #ef4444; font-weight: bold;', error);

        updateMessage(assistantId, {
          content: `이미지 생성 중 오류가 발생했습니다: ${error.message}`,
          isStreaming: false,
          tool: effectiveTool,
          timestamp: new Date().toISOString(),
        });
        setProcessing(false);
        setSelectedTool(null);
      }
    } else if (currentToolConfig?.linkedModelId === 'elevenlabs') {
      // 🎙️ ElevenLabs 오디오 생성 (TTS/Podcast tools) - 백엔드 통해 처리
      console.log('%c[ELEVENLABS] 🎙️ 오디오 생성 모드 (백엔드)', 'color: #6366f1; font-weight: bold;');
      console.groupEnd();

      try {
        // Update message to show generating status
        const statusEmoji = effectiveTool === 'podcast' ? '🎧' : '🎙️';
        const statusText = effectiveTool === 'podcast' ? '팟캐스트를 생성하고 있습니다...' : '음성을 생성하고 있습니다...';
        updateMessage(assistantId, {
          content: `${statusEmoji} ${statusText}`,
          isStreaming: true,
          tool: effectiveTool,
        });

        let audioDataResult = null;

        await processWithBedrock(
          messageToSend,  // inputValue.trim() → messageToSend (퍼널에서는 directMessage 사용)
          effectiveTool, // 'tts' or 'podcast'
          {
            onStart: () => {
              console.log("%c[ELEVENLABS] ✅ 오디오 생성 시작", 'color: #22c55e; font-weight: bold;');
            },
            onChunk: (chunk) => {
              // 진행 상황 메시지 업데이트
              if (chunk) {
                appendStreamingText(chunk);
              }
            },
            onAudioComplete: (audioData) => {
              // 오디오 생성 완료 - 바로 메시지 업데이트
              console.log('%c[AUDIO] 🎙️ 오디오 수신!', 'color: #22c55e; font-weight: bold;', audioData);

              const audioResult = {
                type: audioData.type,
                audioUrl: audioData.audioUrl,
                audioSegments: audioData.audioSegments || [],
                script: audioData.script || [],
                audioDuration: audioData.duration || 0,
                style: audioData.style,
                voice: audioData.voice,
                engine: audioData.engine,
              };

              audioDataResult = audioResult;

              // 오디오가 있는지 확인 (URL 또는 세그먼트)
              const hasAudio = audioResult.audioUrl || (audioResult.audioSegments && audioResult.audioSegments.length > 0);

              // 바로 메시지 업데이트 (audio_complete 받자마자)
              const responseContent = hasAudio
                ? `${statusEmoji} ${effectiveTool === 'podcast' ? 'Podcast' : 'Audio'} has been generated! (${audioResult.engine || 'AI'})`
                : `Generating audio...`;

              updateMessage(assistantId, {
                content: responseContent,
                audioData: audioResult,
                isStreaming: false,
                tool: effectiveTool,
                timestamp: new Date().toISOString(),
              });

              console.log('%c[AUDIO] 메시지 업데이트 완료', 'color: #22c55e;', { hasAudio, segmentCount: audioResult.audioSegments.length });
            },
            onComplete: (result) => {
              console.log('%c[AUDIO] 완료!', 'color: #22c55e; font-weight: bold;');
              finishStreaming();
              setProcessing(false);
              setSelectedTool(null);

              // Update conversation (audioDataResult는 onAudioComplete에서 이미 설정됨)
              if (audioDataResult) {
                const responseContent = `${statusEmoji} ${effectiveTool === 'podcast' ? 'Podcast' : 'Audio'} has been generated!`;
                const updatedMessages = [
                  ...messages,
                  userMessage,
                  {
                    id: assistantId,
                    role: "assistant",
                    content: responseContent,
                    audioData: audioDataResult,
                    tool: effectiveTool,
                    timestamp: new Date().toISOString(),
                  },
                ];
                updateConversationMessages(convId, updatedMessages, true);
                // 백엔드 저장 완료 후 사이드바 목록 갱신
                window.dispatchEvent(new CustomEvent('conversationSaved'));
              }
            },
            onError: (error) => {
              console.log('%c[ELEVENLABS] ❌ 오디오 생성 실패!', 'color: #ef4444; font-weight: bold;', error);
              updateMessage(assistantId, {
                content: `오디오 생성 중 오류가 발생했습니다: ${error.message}`,
                audioData: { type: effectiveTool, error: error.message },
                isStreaming: false,
                tool: effectiveTool,
                timestamp: new Date().toISOString(),
              });
              setProcessing(false);
              setSelectedTool(null);
            },
          },
          'eleven_multilingual_v2', // ElevenLabs 모델 ID
          [], // history not needed for audio
          convId,
          getCurrentUserId(),
          { engine: selectedSubType?.engine }  // 서브타입의 engine 전달
        );

      } catch (error) {
        console.log('%c[ELEVENLABS] ❌ 요청 실패!', 'color: #ef4444; font-weight: bold;', error);

        updateMessage(assistantId, {
          content: `오디오 생성 중 오류가 발생했습니다: ${error.message}`,
          audioData: { type: effectiveTool, error: error.message },
          isStreaming: false,
          tool: effectiveTool,
          timestamp: new Date().toISOString(),
        });
        setProcessing(false);
        setSelectedTool(null);
      }
    } else {
      // Bedrock API mode
      try {
        // Build history from current messages (excluding the streaming placeholder)
        const conversationHistory = messages.map(msg => ({
          role: msg.role,
          content: msg.content
        }));

        // 🔍 DEBUG: API 호출 전 최종 파라미터
        console.log('%c[API] processWithBedrock 호출', 'color: #8b5cf6; font-weight: bold;');
        console.log('%c[API] 전송 파라미터:', 'color: #6b7280;', {
          tool: effectiveTool || "default",
          modelId: selectedModel?.modelId,
          historyCount: conversationHistory.length,
          contentLength: fullContent.length
        });

        await processWithBedrock(
          fullContent, // 첨부 파일 데이터 포함된 전체 내용
          effectiveTool || "default",
          {
            onStart: () => {
              console.log("%c[API] ✅ Bedrock 스트리밍 시작", 'color: #22c55e; font-weight: bold;');
              resetStreaming(); // 🔧 스트리밍 상태 초기화
            },
            onChunk: (chunk, fullText) => {
              // 🔧 React 상태 기반 스트리밍 (nova/title 방식)
              appendStreamingText(chunk);
            },
            onComplete: (result) => {
              // 🔍 DEBUG: 응답 완료
              console.log('%c[API] 응답 완료!', 'color: #22c55e; font-weight: bold; font-size: 12px;');
              console.log('%c[RESULT] 응답 데이터:', 'color: #3b82f6;', {
                contentLength: result.content?.length,
                hasTable: !!result.table,
                hasVisualData: !!result.visualData,
                appliedTool: effectiveTool
              });

              // 🔍 표 데이터 상세 분석
              if (result.table) {
                console.log('%c[RESULT] ⚠️ 표 데이터 발견!', 'color: #ef4444; font-weight: bold;');
                console.table(result.table.headers);
                console.log('행 수:', result.table.rows?.length);
              }

              // 🔍 응답 내용에 표 패턴 검사
              const tablePatterns = ['|', '표:', 'headers:', 'rows:'];
              const foundPatterns = tablePatterns.filter(p => result.content?.includes(p));
              if (foundPatterns.length > 0) {
                console.log('%c[RESULT] 응답에 표 관련 패턴 발견:', 'color: #f59e0b;', foundPatterns);
              }

              console.groupEnd(); // handleSubmit 그룹 종료

              // 🔧 스트리밍 완료 (React 상태 기반)
              finishStreaming();

              // Update final state
              updateMessage(assistantId, {
                content: result.content,
                table: result.table,
                visualData: result.visualData,
                isStreaming: false,
                tool: effectiveTool,
                timestamp: new Date().toISOString(),
              });
              setProcessing(false);
              setSelectedTool(null);

              // Save final messages to sidebar cache using latest store state
              const latestMessages = useChatStore.getState().messages.map(m =>
                m.isStreaming ? { ...m, isStreaming: false } : m
              );
              updateConversationMessages(convId, latestMessages, true);
              // 백엔드 저장 완료 후 사이드바 목록 갱신
              window.dispatchEvent(new CustomEvent('conversationSaved'));
            },
            onUsageWarning: (data) => {
              toast(data.message, { icon: '⚠️', duration: 6000 });
            },
            onError: (error) => {
              console.log('%c[API] ❌ Bedrock 에러!', 'color: #ef4444; font-weight: bold;', error);
              console.groupEnd(); // handleSubmit 그룹 종료
              const demoResult = getDemoResponse(fullContent, effectiveTool);
              updateMessage(assistantId, { ...demoResult, isStreaming: false });
              setProcessing(false);
              setSelectedTool(null);
            },
          },
          selectedModel?.modelId,
          conversationHistory,
          convId,  // conversationId 전달
          getCurrentUserId(),  // 로그인한 사용자의 Cognito sub
          { engine: selectedSubType?.engine }  // 서브타입의 engine 전달
        );
      } catch (error) {
        console.log('%c[API] ❌ 요청 실패!', 'color: #ef4444; font-weight: bold;', error);
        console.groupEnd(); // handleSubmit 그룹 종료
        const demoResult = getDemoResponse(fullContent, effectiveTool);
        updateMessage(assistantId, { ...demoResult, isStreaming: false });
        setProcessing(false);
        setSelectedTool(null);
      }
    }
  }, [inputValue, selectedTool, isProcessing, useDemo, currentConversationId, messages, addMessage, updateMessage, setProcessing, setSelectedTool, setCurrentConversation, addConversation, updateConversationMessages, navigate, attachedFile, resetStreaming, appendStreamingText, finishStreaming, selectedModel, selectedExample, scrollToBottom]);

  const handleExampleClick = (example) => {
    // 클릭하면 선택 상태로 (입력창에 넣지 않음)
    // 이미 같은 예제가 선택되어 있으면 해제
    setSelectedExample(prev => prev === example ? null : example);
  };

  const handleShare = (message) => {
    openShareModal({
      content: message.content,
      table: message.table,
      tool: message.tool,
    });
  };

  // 파일 첨부 핸들러
  const handleFileAttach = (fileData) => {
    setAttachedFile(fileData);
    if (fileData?.type === 'image') {
      setSelectedTool(null); // 이미지는 도구 자동 선택 안 함
    } else {
      setSelectedTool('table'); // 표 도구 자동 선택
    }
  };

  // 도구 선택 핸들러 (chart_convert는 모달로 처리)
  const handleToolSelect = (tool) => {
    if (tool.id === 'chart_convert') {
      setShowChartConverter(true);
    } else if (tool.id === 'chart_slice') {
      setShowChartSlicer(true);
    } else {
      setSelectedTool(tool.id);
    }
  };

  // 파일 첨부 제거 핸들러
  const handleFileRemove = () => {
    setAttachedFile(null);
  };

  // 템플릿 선택 핸들러
  const handleTemplateSelect = (template) => {
    if (template.prompt) {
      setInputValue(template.prompt);
    }
  };

  // 시각화 도구 갤러리 닫기 핸들러
  const handleCloseGallery = () => {
    setSelectedTool(null);
    setSelectedExample(null); // 선택된 샘플도 초기화
  };

  // 도구 선택 해제 핸들러 (샘플도 함께 초기화)
  const handleToolClear = () => {
    setSelectedTool(null);
    setSelectedExample(null);
  };

  // 초기 화면 (메시지 없음) - Gemini 스타일 레이아웃
  // 🔍 DEBUG: 렌더링 시 messages 상태 확인
  console.log('%c[RENDER] messages.length:', 'color: #8b5cf6;', messages.length, '| isProcessing:', isProcessing, '| isLoadingConversation:', isLoadingConversation);

  // 대화 로딩 중일 때 스켈레톤 표시
  if (isLoadingConversation) {
    return <ChatSkeleton />;
  }

  if (messages.length === 0) {
    const currentTool = selectedTool ? getToolById(selectedTool) : null;
    // 시각화 도구 선택 또는 파일 첨부 시 하단 레이아웃 사용
    const isVisualToolSelected = (currentTool && isVisualTool(currentTool.id)) || attachedFile;
    // 팟캐스트 도구 선택 시 퍼널 표시
    const isPodcastSelected = selectedTool === 'podcast';
    // PRISM 도구 선택 시 갤러리 표시
    const isPrismSelected = selectedTool === 'prism';

    // PRISM 선택 시 전용 캔버스 페이지로 이동
    if (isPrismSelected) {
      // PRISM 캔버스로 리다이렉트
      window.location.href = '/prism';
      return null;
    }

    // 팟캐스트 퍼널 표시
    if (isPodcastSelected || podcastFunnel.isActive) {
      // 팟캐스트 도구 선택 시 자동으로 퍼널 시작
      if (isPodcastSelected && !podcastFunnel.isActive) {
        startPodcastFunnel();
      }

      return (
        <div className="flex flex-col h-full">
          <div className="flex-1 flex items-center justify-center overflow-y-auto py-4">
            <div className="w-full max-w-md mx-auto px-4">
              <PodcastFunnel
                onGenerate={(settings) => {
                  // 팟캐스트 생성 요청
                  const styleNames = { news: '뉴스 토크', expert: '전문가 대담', casual: '수다 토크' };
                  const prompt = `[팟캐스트 생성]\n주제: ${settings.topic}\n스타일: ${styleNames[settings.style]}\n길이: ${settings.duration}분\n\n위 설정으로 팟캐스트를 생성해주세요.`;
                  // directMessage로 전달하여 즉시 제출
                  handleSubmit(null, prompt);
                }}
                onCancel={resetPodcastFunnel}
                hideCancel={true}
              />
              {/* 취소 버튼 - 퍼널 콘텐츠 바로 아래에 배치 */}
              <button
                type="button"
                onClick={() => {
                  console.log('Cancel clicked from App.jsx');
                  resetPodcastFunnel();
                  setSelectedTool(null); // 도구 선택도 해제
                }}
                className="w-full mt-4 py-3 text-sm text-text-400 hover:text-text-200 hover:bg-bg-300/50 rounded-xl border border-bg-300/50 transition-all"
              >
                취소
              </button>
            </div>
          </div>
          <ShareModal isOpen={isModalOpen} onClose={closeShareModal} data={shareData} />
        </div>
      );
    }

    // 시각화 도구 선택 시 또는 파일 첨부 시 - Gemini 스타일 (입력창 하단 고정)
    if (isVisualToolSelected) {
      const showTemplateGallery = currentTool && isVisualTool(currentTool.id) && !attachedFile;

      return (
        <div className="flex flex-col h-full">
          {/* 템플릿 갤러리 - 시각화 도구 선택 시에만 표시 (파일 첨부 시 숨김) */}
          <div className="flex-1 overflow-y-auto px-4 py-6">
            <div className="max-w-4xl mx-auto">
              {showTemplateGallery ? (
                <VisualTemplateGallery
                  toolId={currentTool.id}
                  tool={currentTool}
                  onTemplateSelect={handleTemplateSelect}
                  onClose={handleCloseGallery}
                />
              ) : attachedFile ? (
                // 파일 첨부 시 안내 메시지
                <div className="flex flex-col items-center justify-center py-12">
                  {attachedFile.type === 'image' ? (
                    <>
                      <div className="w-48 h-48 rounded-2xl overflow-hidden mb-4 shadow-lg">
                        <img src={attachedFile.dataUrl} alt="첨부 차트" className="w-full h-full object-contain bg-bg-300/30" />
                      </div>
                      <h2 className="text-xl font-bold text-text-100 mb-2">차트 이미지가 첨부되었습니다</h2>
                      <p className="text-text-400 text-center max-w-md">
                        아래 입력창에 원하는 변환 작업을 입력하세요.<br />
                        예: "로고 제거하고 파란색 계열로 바꿔줘"
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center mb-4">
                        <Table className="w-8 h-8 text-emerald-400" />
                      </div>
                      <h2 className="text-xl font-bold text-text-100 mb-2">파일이 첨부되었습니다</h2>
                      <p className="text-text-400 text-center max-w-md">
                        아래 입력창에 원하는 작업을 입력하세요.<br />
                        예: "매출 순으로 정렬해줘", "상위 5개만 표로 만들어줘"
                      </p>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          {/* 입력창 - 하단 고정 (Gemini 스타일) */}
          <div className="sticky bottom-0 bg-bg-100 border-t border-bg-300/30 pt-4 pb-6 px-4">
            <div className="max-w-4xl mx-auto">
              <ChatInput
                value={inputValue}
                onChange={setInputValue}
                onSubmit={handleSubmit}
                isProcessing={isProcessing}
                attachedFile={attachedFile}
                onFileAttach={handleFileAttach}
                onFileRemove={handleFileRemove}
                selectedTool={currentTool}
                selectedSubType={selectedSubType}
                onToolSelect={(tool) => setSelectedTool(tool.id)}
                onToolClear={handleCloseGallery}
                selectedModel={selectedModel}
                onModelSelect={setSelectedModel}
                selectedExample={selectedExample}
                onExampleClear={() => setSelectedExample(null)}
                isInitialScreen={false}
              />
            </div>
          </div>

          {/* Share Modal */}
          <ShareModal isOpen={isModalOpen} onClose={closeShareModal} data={shareData} />

          {/* Chart Converter Modal */}
          <AnimatePresence>
            {showChartConverter && (
              <ChartConverter onClose={() => setShowChartConverter(false)} onSendToChat={handleFileAttach} />
            )}
          </AnimatePresence>
          <AnimatePresence>
            {showChartSlicer && (
              <ChartSlicer onClose={() => setShowChartSlicer(false)} />
            )}
          </AnimatePresence>
        </div>
      );
    }

    // 기본 상태 - 인사말 + 입력창 + 도구 버튼
    return (
      <div className="flex flex-col h-full">
        {/* 상단 여백 - 입력창을 위쪽에 배치 */}
        <div className={`flex-1 flex flex-col items-center pt-[12vh] px-4 transition-all duration-300 ease-out ${
          isAnimatingToChat ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
        }`}>
          {/* 인사말 */}
          <WelcomeGreeting />

          {/* 입력창 + 도구 버튼 + 예제 컨테이너 */}
          <div className="w-full max-w-3xl">

            {/* SubType Description - shown above input */}
            <AnimatePresence mode="wait">
              {selectedSubType?.description && (
                <motion.div
                  key={selectedSubType.id}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18 }}
                  className="mb-3 px-1"
                >
                  <div className="inline-flex items-center gap-2.5 bg-sedaily-500/10 border border-sedaily-500/25 rounded-lg px-3 py-1.5">
                    <div className="w-2 h-2 rounded-full bg-sedaily-400 flex-shrink-0"></div>
                    <span className="text-[13px] font-medium text-text-100">{selectedSubType.description}</span>
                    {selectedSubType.detail && (
                      <>
                        <span className="text-sedaily-500/60 text-[13px]">·</span>
                        <span className="text-[13px] text-text-300">{selectedSubType.detail}</span>
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Chat Input */}
            <div data-tutorial="input">
            <ChatInput
              value={inputValue}
              onChange={setInputValue}
              onSubmit={handleSubmit}
              isProcessing={isProcessing}
              attachedFile={attachedFile}
              onFileAttach={handleFileAttach}
              onFileRemove={handleFileRemove}
              selectedTool={currentTool}
              selectedSubType={selectedSubType}
              onToolSelect={(tool) => setSelectedTool(tool.id)}
              onToolClear={handleToolClear}
              selectedModel={selectedModel}
              onModelSelect={setSelectedModel}
              selectedExample={selectedExample}
              onExampleClear={() => setSelectedExample(null)}
              isInitialScreen={true}
            />
            </div>

            {/* 도구 버튼들 - 입력창 아래 */}
            <ToolButtonsRow
              selectedTool={currentTool}
              onToolSelect={handleToolSelect}
              selectedSubType={selectedSubType}
              onSubTypeSelect={setSelectedSubType}
            />

            {/* 선택된 도구의 가이드 카드 (이미지/인포그래픽/PRISM) */}
            {currentTool?.guide && (
              <div className="pt-4">
                <ToolGuideCard
                  tool={currentTool}
                  selectedAudiences={selectedAudiences}
                  onAudienceToggle={setSelectedAudiences}
                />
              </div>
            )}

            {/* 선택된 도구의 예제들 - 텍스트 도구만 표시 */}
            {currentTool && !currentTool.guide && (
              <ToolExamples
                tool={currentTool}
                onExampleClick={handleExampleClick}
                selectedExample={selectedExample}
              />
            )}
          </div>
        </div>

        {/* Tutorial Guide */}
        <TutorialGuide onComplete={() => console.log('Tutorial completed')} />

        {/* Share Modal */}
        <ShareModal isOpen={isModalOpen} onClose={closeShareModal} data={shareData} />

        {/* Chart Converter Modal */}
        <AnimatePresence>
          {showChartConverter && (
            <ChartConverter onClose={() => setShowChartConverter(false)} onSendToChat={handleFileAttach} />
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showChartSlicer && (
            <ChartSlicer onClose={() => setShowChartSlicer(false)} />
          )}
        </AnimatePresence>
      </div>
    );
  }

  // 대화 진행 중 - 기존 레이아웃 (입력창 하단 고정)
  // 스켈레톤이 Wave 애니메이션 담당, 실제 콘텐츠는 깔끔하게 페이드인
  return (
    <div className="flex flex-col h-full">
      {/* Messages Area - 위에서 아래로 */}
      <div
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto"
        onScroll={handleChatScroll}
      >
        <motion.div
          key={currentConversationId || "default"}
          className="max-w-4xl mx-auto px-6 pt-8 pb-4 w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.12, ease: "easeOut" }}
        >
          {messages.map((message) => (
            <Message
              key={message.id}
              message={message}
              onShare={handleShare}
              streamingText={message.isStreaming ? streamingText : null}
            />
          ))}
          <div ref={messagesEndRef} />
        </motion.div>
      </div>

      {/* Chat Input - 하단 고정 (패딩 추가) */}
      <div className="px-6 pb-4 pt-2 bg-bg-100">
        <div className="max-w-4xl mx-auto">
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSubmit={handleSubmit}
            isProcessing={isProcessing}
            attachedFile={attachedFile}
            onFileAttach={handleFileAttach}
            onFileRemove={handleFileRemove}
            selectedTool={selectedTool ? getToolById(selectedTool) : null}
            selectedSubType={selectedSubType}
            onToolSelect={(tool) => setSelectedTool(tool.id)}
            onToolClear={handleToolClear}
            selectedModel={selectedModel}
            onModelSelect={setSelectedModel}
            selectedExample={selectedExample}
            onExampleClear={() => setSelectedExample(null)}
            isInitialScreen={false}
          />
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal isOpen={isModalOpen} onClose={closeShareModal} data={shareData} />

      {/* Chart Converter Modal */}
      <AnimatePresence>
        {showChartConverter && (
          <ChartConverter onClose={() => setShowChartConverter(false)} onSendToChat={handleFileAttach} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showChartSlicer && (
          <ChartSlicer onClose={() => setShowChartSlicer(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};


// Login Page wrapper
const LoginPage = () => {
  const navigate = useNavigate();

  const handleLogin = (userRole) => {
    navigate("/");
  };

  const handleGoToSignUp = () => {
    navigate("/signup");
  };

  return <LoginContainer onLogin={handleLogin} onGoToSignUp={handleGoToSignUp} />;
};

// Signup Page wrapper
const SignupPage = () => {
  const navigate = useNavigate();

  const handleSignUp = () => {
    navigate("/");
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return <SignUpPage onSignUp={handleSignUp} onBackToLogin={handleBackToLogin} />;
};

// Main App component
function App() {
  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 2000,
          style: {
            background: "hsl(var(--bg-200))",
            color: "hsl(var(--text-100))",
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
          },
        }}
      />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />

        {/* PRISM - 독립 레이아웃 (Layout 사용 안함) */}
        <Route
          path="/prism"
          element={
            <ProtectedRoute>
              <PrismCanvas />
            </ProtectedRoute>
          }
        />

        {/* Admin - 독립 레이아웃 (관리자 전용) */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/usage"
          element={
            <ProtectedRoute>
              <UsageDashboard />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes with Layout */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<ChatView />} />
                  <Route path="/chat/:id" element={<ChatView />} />
                  <Route path="/team" element={<TeamLibrary />} />
                  <Route path="/team/:workId" element={<WorkDetail />} />
                  <Route path="/projects" element={<ProjectList />} />
                  <Route path="/projects/:projectId" element={<ProjectDetail />} />
                  <Route path="/guide" element={<ServiceGuide />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

export default App;
